import { create } from 'zustand';
import {
    createProject as apiCreateProject,
    getProject as apiGetProject,
    listProjects as apiListProjects,
    updateWorkspace as apiUpdateWorkspace,
    archiveProject as apiArchiveProject,
    getProjectEventsFirestore,
    persistEvent,
    renameProject as apiRenameProject,
    generateProjectTitle,
    archiveToFolder as apiArchiveToFolder,
    unarchiveProject as apiUnarchiveProject,
    getWorkspaceState as apiGetWorkspaceState,
    ProjectDocument,
    ProjectSummary,
    type UploadedWorkspaceFile,
    LogicalChainGraph,
    WorkspaceUIState,
    ChatMessage,
    WorkspaceOperation,
    Finding,
    ReferenceMaterials,
    RejectedPath,
    StructuralInsight,
    EffectiveQuery,
    IterationReasoning,
    createEmptyReferenceMaterials,
    ChatEventFirestore,
    MessageSourcesUsed,
    getUserPrefs,
    setUserPrefs,
} from '../apiService';
import {
    WorkspaceType,
    WorkspaceMode,
    WorkspaceSwitchProposal,
    ProductionState,
    ProductionArtifact,
    HandoffSyncNotification,
    DEFAULT_PRODUCTION_STATE,
    DEFAULT_WORKSPACE_SYNC_STATUS,
    DictWorkspaceState,
    DictFinding,
    GroundingFinding,
    GroundingReview,
    ReportGroundingSeal,
    OnlineSupport,
    PlanState,
    InstitutionProfileState,
    DEFAULT_PLAN_STATE,
    DEFAULT_INSTITUTION_PROFILE,
    WorkspaceStateResponse,
    narrowClusterRecord,
} from '../types';
import {
    upsertNode,
    removeNode,
    addEdge,
    removeEdge,
    recordFindingToDictFinding,
} from '../utils/workspaceConversions';

// =============================================================================
// TYPES
// =============================================================================

/**
 * One entry in a turn's activity timeline, as `getDisplayHistory` emits it.
 *
 * `author` / `onBehalfOf` (BAR-1270) are copied verbatim from the source
 * `ChatEventFirestore` and are undefined for every event the server did not
 * stamp — which is all of them until an external agent writes one. The chat
 * timeline treats undefined as "identity implied by type", i.e. unchanged.
 */
export interface ActivityLogEntry {
    type: string;
    content: string;
    timestamp: number;
    author?: ChatEventFirestore['author'];
    onBehalfOf?: string;
}

export function buildDictWorkspaceStateFromResponse(state: WorkspaceStateResponse): DictWorkspaceState {
    const nodes = Object.entries(state.nodes || {}).reduce<DictWorkspaceState['nodes']>((acc, [nodeId, node]) => {
        acc[nodeId] = {
            ...node,
            cluster_id: (node as any).cluster_id ?? null,
        };
        return acc;
    }, {});
    const models = Object.entries(state.models || {}).reduce<DictWorkspaceState['models']>((acc, [modelId, model]) => {
        acc[modelId] = {
            ...model,
            nodes: (model.nodes || []).map((node) => ({
                ...node,
                cluster_id: (node as any).cluster_id ?? null,
            })),
        };
        return acc;
    }, {});

    return {
        workspace_id: state.workspace_id,
        sequence_number: state.sequence_number,
        compartment_id: state.compartment_id,
        findings: state.findings || {},
        nodes,
        edges: state.edges || {},
        edge_metadata: state.edge_metadata,
        markers: state.markers || {},
        reports: state.reports || {},
        clusters: narrowClusterRecord((state as any).clusters),
        models,
        doc_files: state.doc_files || {},
        output_package_meta: state.output_package_meta ?? null,
        online_support: state.online_support || {},
        uploaded_files: state.uploaded_files || {},
        // BAR-192: grounding seal rides along with /state (Phase B emits it;
        // the report-library reducer derives `stale` on report mutations).
        report_grounding_seal: state.report_grounding_seal ?? null,
        report_review: state.report_review ?? null,
        // BAR-505: sealed regulatory scope (research workspaces) rides /state;
        // same forgot-to-surface guard as report_grounding_seal.
        regulatory_scope: state.regulatory_scope ?? null,
        // #3: upstream workspace OUTPUTS (plan-graph join, derived read-time).
        linked_outputs: state.linked_outputs || [],
        project: state.project ?? null,
        grounding_reviews: state.grounding_reviews ?? {},
        auto_repair: state.auto_repair ?? null,
        derived_at: state.derived_at,
    };
}

/** BAR-192 Phase C — flatten a grounding seal into display findings for the
 *  Report view. Returns [] for a missing or STALE seal: a stale seal's line
 *  anchors refer to a previous report version, so nothing may highlight.
 *  Everything is `severity: 'suspected'` until Phase D adjudicates.
 *
 *  `reportContent` (the raw markdown the seal was computed on) enables
 *  line-anchoring for DANGLING refs: the seal itemises them as marker ids
 *  only (no line numbers), but the `{ref:id}` token is findable in the
 *  content — the same scan the detector did. Without content, dangling
 *  refs still count in the badge but render no highlight. */
export function normalizeGroundingFindings(
    seal: ReportGroundingSeal | null | undefined,
    reportContent?: string | null,
): GroundingFinding[] {
    if (!seal || seal.stale) return [];
    const out: GroundingFinding[] = [];
    // Phase D: items carry their adjudicated severity once validate_work has
    // run the second reader. 'cleared' findings flow through WITH their
    // severity (post-run visual states, Nicholas 2026-06-12): the renderer
    // shows them struck-through so the user sees what validation resolved,
    // instead of flags silently vanishing.
    for (const c of seal.presence?.uncited_claims ?? []) {
        if (typeof c?.line !== 'number') continue;
        const item = applyReview({
            line: c.line,
            kind: 'uncited_claim',
            detail: `Uncited claim: "${c.text}"`,
            severity: c.severity ?? 'suspected',
            adjudication: c.adjudication,
            review: c.review,
            line_sha1: c.line_sha1,
        });
        if (item) out.push(item);
    }
    for (const f of seal.correspondence_findings ?? []) {
        if (typeof f?.line !== 'number') continue;
        const item = applyReview({
            line: f.line,
            kind: f.kind || 'correspondence',
            detail: f.detail || '',
            severity: f.severity ?? 'suspected',
            adjudication: f.adjudication,
            review: f.review,
            line_sha1: f.line_sha1,
        });
        if (item) out.push(item);
    }
    const dangling = seal.presence?.dangling_markers ?? [];
    if (dangling.length > 0 && reportContent) {
        const lines = reportContent.split('\n');
        for (const markerId of dangling) {
            const token = `{ref:${markerId}}`;
            const adj = seal.adjudication?.dangling?.[markerId];
            lines.forEach((lineText, i) => {
                if (lineText.includes(token)) {
                    const item = applyReview({
                        line: i + 1, // detector lines are 1-indexed
                        kind: 'dangling_reference',
                        detail: `Citation {ref:${markerId}} does not resolve to any retrieved source — the reference may be fabricated.`,
                        severity: adj ? (adj.severity ?? 'confirmed') : 'suspected',
                        adjudication: adj
                            ? { verdict: adj.verdict, rationale: adj.rationale, quote: adj.quote }
                            : undefined,
                        review: adj?.review,
                        marker_id: markerId,
                    });
                    if (item) out.push(item);
                }
            });
        }
    }
    // Phase D sweep findings (meaning-level errors with no deterministic
    // flag) arrive confirmed directly, per the locked two-tier design.
    for (const f of seal.adjudication?.sweep_findings ?? []) {
        if (typeof f?.line !== 'number' || f.line < 1) continue;
        const item = applyReview({
            line: f.line,
            kind: f.kind || 'semantic',
            detail: f.detail || '',
            severity: 'confirmed',
            adjudication: f.adjudication,
            review: f.review,
            line_sha1: (f as { line_sha1?: string }).line_sha1,
        });
        if (item) out.push(item);
    }
    return out;
}

/** BAR-192 Phase E — fold a persisted human decision into a finding's
 *  effective display state (the locked semantics):
 *    confirmed + rejected → amber again, marked dismissed;
 *    cleared  + accepted → fully resolved (renders nothing);
 *    cleared  + rejected → amber again (user disagrees with the clearing);
 *    confirmed + accepted → stays confirmed, gains `fixable` (Fix button).
 *  No review → unchanged. Returns null when the finding should not render. */
function applyReview(f: GroundingFinding): GroundingFinding | null {
    const review = f.review;
    if (!review) return f;
    // Phase F repair states first — they apply across severities.
    if (review.decision === 'accepted_internal') {
        // Human ruled it institution-internal: the amber leaves the report
        // entirely (the drawer's Internal group keeps the record).
        return null;
    }
    if (review.decision === 'fix_requested') {
        // Still on the seal => the fix hasn't landed yet (or failed).
        return { ...f, fixable: true, fixPending: true };
    }
    if (review.decision === 'source_requested') {
        return { ...f, sourcePending: true };
    }
    if (review.decision === 'good_enough') {
        // Auto-repair: the nit-judge ruled this surviving red a refinement nit.
        // It leaves the report highlights (resolved) — the drawer's
        // "Resolved — minor" group keeps the record + the override.
        return null;
    }
    if (f.severity === 'cleared') {
        return review.decision === 'accepted'
            ? null
            : { ...f, severity: 'suspected' };
    }
    if (f.severity === 'confirmed') {
        return review.decision === 'accepted'
            ? { ...f, fixable: true }
            : { ...f, severity: 'suspected', dismissed: true };
    }
    return f;
}

/** BAR-192 Phase E — review progress over the decidable findings
 *  (confirmed or cleared; unverifiable ambers carry nothing to decide).
 *  Counted from the itemized lists, so capped lists cap the counter too —
 *  the seal's `truncated` flag stays the honesty signal. */
export function groundingReviewProgress(
    seal: ReportGroundingSeal | null | undefined,
): { decidable: number; reviewed: number; awaiting: number } {
    let decidable = 0;
    let reviewed = 0;
    if (seal && !seal.stale && seal.adjudication?.ran) {
        const tally = (sev?: string, review?: unknown) => {
            if (sev !== 'confirmed' && sev !== 'cleared') return;
            decidable += 1;
            if (review) reviewed += 1;
        };
        for (const c of seal.presence?.uncited_claims ?? []) tally(c.severity, c.review);
        for (const f of seal.correspondence_findings ?? []) tally(f.severity, f.review);
        for (const f of seal.adjudication?.sweep_findings ?? []) {
            tally(f.severity ?? 'confirmed', f.review);
        }
        for (const d of Object.values(seal.adjudication?.dangling ?? {})) {
            tally(d.severity ?? 'confirmed', d.review);
        }
    }
    return { decidable, reviewed, awaiting: Math.max(0, decidable - reviewed) };
}

/** BAR-192 Phase E — badge counts AFTER review decisions are applied:
 *  a rejected confirmed/cleared verdict renders amber again, so it must
 *  count as suspected, not confirmed — otherwise rejecting everything
 *  would flip the badge to "Validated" while amber highlights remain.
 *  When no reviews exist, falls back to the seal's exact block counts
 *  (preserves #297 behavior incl. truncation exactness). */
export function groundingEffectiveCounts(
    seal: ReportGroundingSeal | null | undefined,
): { confirmed: number; suspected: number } {
    const blockConfirmed = groundingConfirmedCount(seal);
    const blockSuspected = groundingSuspectedCount(seal);
    if (!seal || seal.stale || !seal.adjudication?.ran) {
        return { confirmed: blockConfirmed, suspected: blockSuspected };
    }
    // Any review anywhere flips us to list-based counting — the progress
    // counter only tracks confirmed/cleared decisions, but Phase F adds
    // decisions on suspected findings (accepted_internal/source_requested).
    const hasReviews =
        (seal.presence?.uncited_claims ?? []).some((c) => c.review) ||
        (seal.correspondence_findings ?? []).some((f) => f.review) ||
        (seal.adjudication?.sweep_findings ?? []).some((f) => f.review) ||
        Object.values(seal.adjudication?.dangling ?? {}).some((d) => d.review);
    if (!hasReviews) {
        return { confirmed: blockConfirmed, suspected: blockSuspected };
    }
    let confirmed = 0;
    let suspected = 0;
    const tally = (sev: string | undefined, review?: { decision?: string }) => {
        const severity = sev ?? 'suspected';
        if (review?.decision === 'good_enough') {
            // Auto-repair: nit-resolved — no longer counts against the report
            // (the nit-judge declared it a refinement, not a real issue).
            return;
        }
        if (severity === 'confirmed') {
            if (review?.decision === 'rejected') suspected += 1;
            else confirmed += 1;
        } else if (severity === 'cleared') {
            if (review?.decision === 'rejected') suspected += 1;
            // accepted or undecided cleared: resolved/strike-through, not amber
        } else if (review?.decision === 'accepted_internal') {
            // Phase F: human ruled the amber institution-internal — it no
            // longer counts against the report (internals alone must not
            // block "Validated").
        } else {
            suspected += 1;
        }
    };
    for (const c of seal.presence?.uncited_claims ?? []) tally(c.severity, c.review);
    for (const f of seal.correspondence_findings ?? []) tally(f.severity, f.review);
    for (const f of seal.adjudication?.sweep_findings ?? []) {
        tally(f.severity ?? 'confirmed', f.review);
    }
    for (const d of Object.values(seal.adjudication?.dangling ?? {})) {
        tally(d.severity ?? 'confirmed', d.review);
    }
    return { confirmed, suspected };
}

/** BAR-192 Phase F — one repaired/in-flight outcome entry for the drawer. */
export interface GroundingRepairedRow {
    identityKey: string;
    kind: string;
    verdict: string;
    decision: 'fix_requested' | 'source_requested';
    /** Honest per-kind outcome: 'fixed' (a deterministic detector re-ran and
     *  no longer finds it), 'edited' (sweep finding on a carried seal — the
     *  sentence changed but nothing has re-judged the new text yet), or
     *  'resolved' (a source_requested amber that re-validation retired). */
    outcome: 'fixed' | 'edited' | 'resolved';
    actor_id: string;
    decided_at: string;
}

/** Derive repair outcomes: decisions whose identity no longer matches any
 *  finding on the CURRENT seal. Identity still present = in-flight/failed —
 *  that renders on the live row, not here. */
export function groundingRepairedRows(
    seal: ReportGroundingSeal | null | undefined,
    reviews: Record<string, GroundingReview> | null | undefined,
): GroundingRepairedRow[] {
    if (!seal || seal.stale || !seal.adjudication?.ran || !reviews) return [];
    const present = new Set<string>();
    const add = (kind: string, anchor?: string, verdict?: string) => {
        if (kind && anchor && verdict) present.add(`${kind}|${anchor}|${verdict}`);
    };
    for (const c of seal.presence?.uncited_claims ?? []) {
        add('uncited_claim', (c as { line_sha1?: string }).line_sha1, c.adjudication?.verdict);
    }
    for (const f of seal.correspondence_findings ?? []) {
        add(f.kind || 'correspondence', (f as { line_sha1?: string }).line_sha1, f.adjudication?.verdict);
    }
    for (const f of seal.adjudication?.sweep_findings ?? []) {
        add(f.kind || 'semantic', (f as { line_sha1?: string }).line_sha1, f.adjudication?.verdict);
    }
    for (const [markerId, d] of Object.entries(seal.adjudication?.dangling ?? {})) {
        add('dangling_reference', markerId, (d as { verdict?: string }).verdict);
    }

    const out: GroundingRepairedRow[] = [];
    for (const [key, review] of Object.entries(reviews)) {
        if (review.decision !== 'fix_requested' && review.decision !== 'source_requested') continue;
        if (present.has(key)) continue; // in-flight/failed — lives on the row
        const [kind = '', , verdict = ''] = key.split('|');
        // 'fixed'/'resolved' are VERIFICATION claims — only a seal minted by
        // an actual validation run (fresh or verify-scope) may make them.
        // A merely-carried seal proves the sentence changed, nothing more
        // (third-soak finding: an edit was labeled ✓ fixed while its
        // replacement sentence sat re-flagged, unjudged).
        const verified = Boolean(seal.adjudication?.ran && !seal.adjudication?.carried);
        const outcome: GroundingRepairedRow['outcome'] = !verified
            ? 'edited'
            : review.decision === 'source_requested'
                ? 'resolved'
                : kind.startsWith('semantic_') && seal.adjudication?.sweep_skipped
                    ? 'edited'
                    : 'fixed';
        out.push({
            identityKey: key,
            kind,
            verdict,
            decision: review.decision,
            outcome,
            actor_id: review.actor_id,
            decided_at: review.decided_at,
        });
    }
    return out;
}

/** BAR-192 auto-repair — one nit-resolved ("good-enough") entry for the
 *  drawer's "Resolved — minor" group. */
export interface GroundingNitRow {
    identityKey: string;
    line: number;
    kind: string;
    verdict: string;
    detail: string;
    actor_id: string;
    decided_at: string;
}

/** Derive the nit-resolved findings: still on the seal (confirmed) but carrying
 *  a good_enough decision — the nit-judge ruled the surviving objection a
 *  refinement, not a real issue. They render in the drawer's "Resolved — minor"
 *  group with a "this actually matters" override (which submits `accepted`). */
export function groundingNitRows(
    seal: ReportGroundingSeal | null | undefined,
): GroundingNitRow[] {
    if (!seal || seal.stale || !seal.adjudication?.ran) return [];
    const out: GroundingNitRow[] = [];
    // Structural param: seal findings carry severity OPTIONALLY (and uncited
    // claims have no severity at all), so don't demand a full GroundingFinding.
    const collect = (
        f: {
            line: number;
            detail?: string;
            line_sha1?: string;
            adjudication?: { verdict?: string; rationale?: string };
            review?: { decision?: string; actor_id?: string; decided_at?: string };
        },
        kind: string,
    ) => {
        if (f.review?.decision !== 'good_enough') return;
        out.push({
            identityKey: `${kind}|${f.line_sha1 ?? ''}|${f.adjudication?.verdict ?? ''}`,
            line: f.line,
            kind,
            verdict: f.adjudication?.verdict ?? '',
            detail: f.detail || f.adjudication?.rationale || '',
            actor_id: f.review?.actor_id ?? '',
            decided_at: f.review?.decided_at ?? '',
        });
    };
    for (const c of seal.presence?.uncited_claims ?? []) collect(c, 'uncited_claim');
    for (const f of seal.correspondence_findings ?? []) collect(f, f.kind || 'correspondence');
    for (const f of seal.adjudication?.sweep_findings ?? []) collect(f, f.kind || 'semantic');
    return out;
}

/** BAR-192 Phase D — confirmed-issue count for the badge's danger state.
 *  0 when the adjudicator hasn't run. */
export function groundingConfirmedCount(
    seal: ReportGroundingSeal | null | undefined,
): number {
    return seal?.adjudication?.ran ? (seal.adjudication.confirmed_count ?? 0) : 0;
}

/** BAR-192 Phase D — how many deterministic findings REMAIN unresolved
 *  (suspected) after adjudication. Sweep findings are added confirmed issues,
 *  not part of the deterministic denominator, so exclude them from the
 *  resolved subtraction. Equals the plain finding count when the adjudicator
 *  hasn't run. */
export function groundingSuspectedCount(
    seal: ReportGroundingSeal | null | undefined,
): number {
    const total = groundingFindingCount(seal);
    if (!seal?.adjudication?.ran) return total;
    const sweepConfirmed = seal.adjudication.sweep_findings?.length ?? 0;
    const deterministicResolved = Math.max(0,
        (seal.adjudication.cleared_count ?? 0) +
        (seal.adjudication.confirmed_count ?? 0) -
        sweepConfirmed,
    );
    return Math.max(0, total - deterministicResolved);
}

/** BAR-192 Phase C — total findings behind the badge count: uncited claims +
 *  dangling refs + correspondence findings. Counts (not capped lists) so the
 *  badge stays exact even when the seal's itemised lists were truncated. */
export function groundingFindingCount(
    seal: ReportGroundingSeal | null | undefined,
): number {
    if (!seal) return 0;
    return (
        (seal.presence?.uncited_claim_count ?? 0) +
        (seal.presence?.dangling_markers?.length ?? 0) +
        (seal.correspondence_finding_count ?? 0)
    );
}

/**
 * Hydrate the InstitutionProfileState slice from a loaded workspace state.
 *
 * Returns the hydrated profile when the workspace carries any institution
 * context (profile fields or regulatory choices), otherwise `undefined` so
 * callers can leave the existing store slice untouched.
 *
 * Shared by `loadProject` (initial project open) and `loadSpecificWorkspace`
 * (drilling into the institution_context workspace via the plan train track
 * / plan-node double-click). Keeping a single converter ensures the panel
 * shows the same data regardless of how the user reached the phase.
 */
export function hydrateInstitutionProfileFromState(
    state: WorkspaceStateResponse,
): InstitutionProfileState | undefined {
    const profFields = state.profile_fields || {};
    const regChoices = state.regulatory_choices || {};
    const keyFigs = state.key_figures || {};
    const openItems = state.open_items || {};
    const hasInstitution =
        Object.keys(profFields).length > 0 || Object.keys(regChoices).length > 0;
    if (!hasInstitution) return undefined;

    return {
        profile_fields: Object.values(profFields).map((f: any) => ({
            field_id: f.field_id,
            label: f.label,
            value: f.value,
            confidence: f.confidence || 'confirmed',
        })),
        regulatory_choices: Object.values(regChoices).map((c: any) => ({
            choice_id: c.choice_id,
            area: c.area,
            approach: c.approach,
            scope_note: c.scope_note || '',
            confidence: c.confidence || 'confirmed',
        })),
        key_figures: Object.values(keyFigs).map((k: any) => ({
            figure_id: k.figure_id,
            metric: k.metric,
            value: k.value,
            as_of_date: k.as_of_date,
            source: k.source || 'user_stated',
        })),
        open_items: Object.values(openItems).map((o: any) => ({
            item_id: o.item_id,
            description: o.description,
            related_to: o.related_to,
            status: o.status || 'open',
            resolution: o.resolution,
        })),
        is_ready: state.institution_context_ready || false,
    };
}

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error';

/**
 * Hard bound on how far `resyncFloor` may rewind below the watermark.
 *
 * Sized against the workload that motivated BAR-248: prod's busiest project
 * grew ~100 events/day, so 200 is roughly two days of that project's activity
 * — comfortably longer than the ~1h research TTL after which a background
 * PATCH can no longer arrive, and short enough that a 3,900-event log is never
 * re-read in full.
 */
const MAX_RESYNC_REWIND = 200;

/**
 * Chat events fetched per page.
 *
 * Opening a project reads one page (the newest), not the whole log. 200 is
 * several screens of history — enough that most sessions never page again —
 * while bounding the cold open regardless of how long the project runs. It
 * matches the server's own default (`DEFAULT_EVENT_PAGE_SIZE`); the server
 * caps the request at 2,000 either way.
 */
const EVENT_PAGE_SIZE = 200;

/**
 * Lowest sequence an incremental event read must start BELOW (BAR-248).
 *
 * A tail read is only sound if the log is append-only, and report-library's
 * is not: `update_event` PATCHes an event's `content` in place and leaves its
 * `sequence` untouched (`database.py` `update_event`), while `get_events`
 * filters strictly on `sequence > since_sequence`. So an event that is
 * rewritten after a client has read past it can never be re-read by that
 * client.
 *
 * The one case where that rewrite lands long after the fact is background
 * research: the orchestrator posts a `research_progress` placeholder, the turn
 * completes, and only minutes later does the background task PATCH that same
 * event with the finished snapshot. A pure watermark read would leave the
 * placeholder on screen — and in the audit trail — for the rest of the
 * session. (Streaming's `thinking`/`response` PATCHes are a smaller case: for
 * a single user they have settled by the time a re-sync runs, since re-sync is
 * triggered BY completion — but with two users in one project, A's re-sync can
 * land mid-B's turn and pass over B's still-accumulating `response`. What
 * actually covers that is the -1 margin below, because the event being
 * rewritten is the newest one. Anyone extending this floor should know the
 * margin is doing that work, not the completion trigger.)
 *
 * So: rewind below any `research_progress` this client holds that has not yet
 * reported `isActive: false`. Unparseable content counts as still-open, since
 * the safe direction is to re-read. The floor is self-clearing — once the
 * finished snapshot arrives, that event stops pulling the read back — and it
 * is bounded by the oldest still-running research rather than by the start of
 * the log, so it keeps the O(all events) read off the hot path.
 *
 * The extra 1 below that is an unrelated, nearly-free safety margin:
 * `get_next_sequence` is a non-transactional last+1 read-then-write, so two
 * concurrent writers can both land sequence N. Re-reading the last event every
 * time lets the second one through. Callers dedupe by `event_id`, so the
 * re-read costs one redundant row, not a duplicate message.
 */
export function resyncFloor(events: ChatEventFirestore[], watermark: number): number {
    let floor = watermark;
    for (const e of events) {
        if (e.type !== 'research_progress' || e.sequence > watermark) continue;
        let finished = false;
        try {
            finished = JSON.parse(e.content)?.isActive === false;
        } catch {
            finished = false;
        }
        if (!finished) floor = Math.min(floor, e.sequence - 1);
    }
    // Bound how far an open placeholder can drag the read back.
    //
    // An earlier attempt settled a placeholder when a later `tool_result`
    // mentioned `research_workflow`. That was wrong twice over, and both were
    // checked against the running system rather than reasoned about:
    //
    //   - It never matched. A persisted tool_result's `content` is the
    //     ENRICHED SUMMARY ("1 results found. Article 4. …") and its metadata
    //     is only `{success}` — the tool name is nowhere in it. Confirmed on
    //     dev: 64 persisted tool_results on one project, zero mentioning
    //     `research_workflow`. (`getDisplayHistory`'s identical heal at the
    //     bottom of this file is inert for the same reason.)
    //   - If it HAD matched it would have been unsound. Under
    //     `USE_BACKGROUND_RESEARCH=true` the tool_result is persisted when
    //     research LAUNCHES, immediately after the placeholder, so treating it
    //     as completion would settle a placeholder whose PATCH is still
    //     minutes away — reintroducing the stranded-snapshot bug this floor
    //     exists to prevent.
    //
    // There is no client-visible signal that separates "inline, will never be
    // PATCHed" from "background, PATCH pending", so don't try to infer it.
    // Bound the cost instead: a background PATCH lands within the research
    // TTL (~1h), which is far inside this many events, while an inline
    // placeholder — which is what dev and prod actually produce, since
    // USE_BACKGROUND_RESEARCH is unset in both — can no longer pin the read to
    // the start of the log for the life of the project.
    return Math.max(0, Math.max(floor, watermark - MAX_RESYNC_REWIND) - 1);
}

/** Merge a fetched tail over a confirmed prefix, preferring the server's copy. */
function mergeEventTail(
    held: ChatEventFirestore[],
    since: number,
    fetched: ChatEventFirestore[],
): ChatEventFirestore[] {
    const fetchedIds = new Set(fetched.map(e => e.event_id));
    // Drop anything the server just re-sent (the rewind above overlaps on
    // purpose) so a re-read never renders twice, and drop everything above the
    // watermark, which is exactly the optimistic set `addLocalEvent` created.
    const kept = held.filter(e => e.sequence <= since && !fetchedIds.has(e.event_id));
    // Mirror the backend's own ordering contract — timestamp first, sequence as
    // the tie-break — because a rewind can pull re-read rows back below the
    // prefix's tail, so plain concatenation is no longer sorted.
    return [...kept, ...fetched].sort((a, b) => {
        const ta = Date.parse(a.timestamp), tb = Date.parse(b.timestamp);
        if (ta !== tb && !Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
        return a.sequence - b.sequence;
    });
}

interface ProjectState {
    // State
    activeProject: ProjectDocument | null;
    projectList: ProjectSummary[];
    // Whose projects `projectList` holds: the userId of the last successful
    // loadProjectList. Changing the signed-in user (impersonation start or
    // exit) leaves the previous user's list here until the next load.
    projectListUserId: string | null;
    totalProjects: number;
    syncStatus: SyncStatus;
    syncError: string | null;
    lastSyncedAt: Date | null;
    pendingChanges: boolean;
    isLoading: boolean;
    listLoading: boolean;
    /** The user whose last active-list load failed, else null. Lets a page
     *  say "could not be loaded" instead of showing a failed list as empty. */
    listFailedFor: string | null;
    // True while a specific workspace/compartment is being fetched in response
    // to a direct user navigation (plan-node drill, "Open node"). Lets the UI
    // show an immediate loading state instead of a silent stale view (#89).
    workspaceLoading: boolean;

    // Dict-based workspace state (from Report Library event store)
    // This is the single source of truth for workspace data
    dictWorkspace: DictWorkspaceState | null;

    // Bumped when a `requirements_updated` / `validation_complete` workspace
    // op arrives (agent linked / marked done / validated requirements).
    // Requirement state lives server-side (the joined /requirements
    // projection), so these ops carry no dict payload — open Spec views key
    // their re-fetch on this counter instead.
    requirementsRefreshSignal: number;

    // Firestore events subcollection state
    firestoreEvents: ChatEventFirestore[];
    lastEventSequence: number;
    /**
     * High-water mark of events the BACKEND has confirmed (BAR-248).
     *
     * Distinct from `lastEventSequence`, which `addLocalEvent` also advances
     * for optimistic events that may never be persisted with that number. Only
     * a backend response moves this one, which makes it the only value safe to
     * pass as `since_sequence` — using `lastEventSequence` would skip real
     * events whenever an optimistic one had run ahead of the server.
     */
    lastPersistedSequence: number;
    eventsLoading: boolean;
    /**
     * Oldest sequence the loaded history covers, or 0 when the whole log is
     * loaded. Opening a project reads only the newest page, so this is the
     * cursor `loadEarlierEvents` pages backwards from — and `> 1` is what
     * tells the UI there is older history to offer.
     */
    oldestLoadedSequence: number;
    earlierEventsLoading: boolean;

    // Multi-workspace state
    activeWorkspace: WorkspaceType;
    workspaceMode: WorkspaceMode;           // User's viewport (per-user, local)
    barkWorkspace: WorkspaceMode;           // Where BARK is currently working (shared, broadcast to all users)
    barkWorkspaceName: string;              // Plan node name BARK is in (e.g. "NMD requirements")
    workspaceSyncStatus: Record<WorkspaceType, SyncStatus>;
    pendingWorkspaceSwitch: WorkspaceSwitchProposal | null;
    productionState: ProductionState;

    // Task & Institution Context workspace state
    plan: PlanState;
    institutionProfile: InstitutionProfileState;

    // Internal state
    _currentUserId: string | null;  // Current user's email (for per-user prefs)
    _saveTimeoutId: ReturnType<typeof setTimeout> | null;
    _currentVersion: number;
    _changeRevision: number;
    _projectEpoch: number;
    _savePromise: Promise<boolean> | null;
    _saveProjectId: string | null;

    // Actions
    createProject: (userId: string, name: string, description?: string, projectType?: 'research' | 'build', options?: { skipReset?: boolean }) => Promise<ProjectDocument | null>;
    loadProject: (projectId: string, userId: string, opts?: { preserveViewport?: boolean; discardPendingChanges?: boolean }) => Promise<boolean>;
    loadProjectList: (userId: string, page?: number, append?: boolean, pageSize?: number, light?: boolean) => Promise<boolean>;
    updateWorkspace: (workspace: LogicalChainGraph, uiState?: WorkspaceUIState) => void;
    updateReferenceMaterials: (referenceMaterials: ReferenceMaterials) => void;
    saveWorkspaceNow: (userId: string) => Promise<boolean>;
    archiveProject: (projectId: string, userId: string) => Promise<boolean>;
    // User-facing Archive FOLDER (status=archived_visible) — distinct from the
    // hidden soft-delete above. archivedProjectList backs the "Archived" tab.
    archivedProjectList: ProjectSummary[];
    // Cache flags for the Archived tab so it doesn't re-fetch on every open.
    // archivedLoaded: the list is current; archivedLoading: a fetch is in flight
    // (drives the tab's loading spinner). Invalidated whenever the active list
    // reloads (any mutation refreshes it), so a stale overflow can't persist.
    archivedLoaded: boolean;
    archivedLoading: boolean;
    loadArchivedProjects: (userId: string) => Promise<boolean>;
    // Count of manually-archived projects (status=archived_visible), fetched
    // cheaply on load so the Archived badge shows a number before the tab is
    // opened. The full list stays lazy; only this count is eager. The badge
    // adds the recency overflow (active beyond the cap) on top, computed in the
    // view from totalProjects.
    archivedVisibleCount: number;
    loadArchivedCount: (userId: string) => Promise<void>;
    shelveProject: (projectId: string, userId: string) => Promise<boolean>;
    unshelveProject: (projectId: string, userId: string) => Promise<boolean>;
    duplicateProject: (projectId: string, userId: string) => Promise<{ success: boolean; new_project_id?: string; error?: string; warnings?: any; skipped_non_canonical_notebooks?: string[] }>;
    clearPendingRename: () => void;
    clearActiveProject: (options?: { discardPendingChanges?: boolean }) => Promise<boolean>;
    setSyncStatus: (status: SyncStatus, error?: string) => void;

    // Duplicate state
    duplicateJobs?: { [sourceProjectId: string]: { jobId: string; newProjectId?: string; phase?: string } };
    pendingRenameProjectId?: string | null;

    // Multi-workspace actions
    setActiveWorkspace: (workspace: WorkspaceType) => void;
    setWorkspaceMode: (mode: WorkspaceMode, initiator?: 'user' | 'agent') => void;

    // BAR-523: files attached when STARTING a project are uploaded during
    // creation and stashed here, keyed by project id, so GeminiChatArea's
    // auto-sent first message can attach them. Project-id-keyed + consume-once so
    // a stale set can never leak onto a different project's opening turn.
    firstTurnUploads: { projectId: string; files: UploadedWorkspaceFile[] } | null;
    setFirstTurnUploads: (value: { projectId: string; files: UploadedWorkspaceFile[] } | null) => void;
    consumeFirstTurnUploads: (projectId: string) => UploadedWorkspaceFile[];
    setBarkWorkspace: (mode: WorkspaceMode, expectedProjectId?: string) => void;
    setBarkWorkspaceName: (name: string, expectedProjectId?: string) => void;
    setMainWorkspaceView: (wsType: string) => void;
    proposeWorkspaceSwitch: (proposal: WorkspaceSwitchProposal) => void;
    acceptWorkspaceSwitch: () => void;
    rejectWorkspaceSwitch: () => void;
    setProductionDocument: (type: 'doc' | 'sheet', url: string | null) => void;
    selectFindingForProduction: (findingId: string) => void;
    deselectFindingForProduction: (findingId: string) => void;
    setHandoffSyncNotification: (notification: HandoffSyncNotification | null) => void;
    clearHandoffSyncNotification: () => void;
    setActiveArtifact: (artifactId: string | null) => void;

    // Task & Institution Context actions
    updatePlan: (plan: Partial<PlanState>) => void;
    updateInstitutionProfile: (profile: Partial<InstitutionProfileState>) => void;

    // Chat history actions
    addChatMessage: (role: 'user' | 'assistant', content: string, metadata?: Record<string, any>) => void;
    addChatMessageLocal: (role: 'user' | 'assistant', content: string, metadata?: Record<string, any>) => void;
    getChatHistory: () => ChatMessage[];

    // Workspace operations (from API responses)
    applyWorkspaceOperations: (operations: WorkspaceOperation[], expectedProjectId?: string) => void;

    // User finding actions (from Search tab)
    addUserFinding: (finding: DictFinding) => void;

    // Dict workspace state actions
    /** `fresh` bypasses request coalescing — pass it when this read exists to
     *  observe the server's state after a write this client just made. */
    loadWorkspaceState: (projectId: string, userId: string, opts?: { fresh?: boolean }) => Promise<boolean>;
    loadSpecificWorkspace: (workspaceId: string, userId: string, opts?: {
        showLoading?: boolean;
        expectedProjectEpoch?: number;
        expectedChangeRevision?: number;
        /** See `loadWorkspaceState`. */
        fresh?: boolean;
    }) => Promise<boolean>;
    getDictWorkspace: () => DictWorkspaceState | null;

    // Firestore events subcollection actions
    loadProjectEvents: (projectId: string, userId: string) => Promise<boolean>;
    reloadProjectEvents: (projectId: string, userId: string) => Promise<boolean>;
    /** Prepend the page of history just older than `oldestLoadedSequence`. */
    loadEarlierEvents: (projectId: string, userId: string) => Promise<boolean>;
    clearProjectEvents: () => void;
    getProjectEvents: () => ChatEventFirestore[];
    addLocalEvent: (event: Omit<ChatEventFirestore, 'event_id' | 'sequence'> & Partial<Pick<ChatEventFirestore, 'event_id' | 'sequence'>>) => void;
    // BAR-320: replay a prior (home-page) conversation onto a freshly created
    // project as turn events, so the discussion the user had before entering
    // research both renders in the chat and, once persisted, is part of the
    // history the orchestrator loads from the event log (BAR-1551).
    seedConversationHistory: (projectId: string, userId: string, turns: Array<{ role: 'user' | 'assistant'; content: string }>) => void;
    // BAR-320 follow-up: best-effort smart project name. Every create-from-chat
    // path calls this right after createProject; the project shows its placeholder
    // name until the title resolves, then renames. Never throws.
    autoTitleProject: (projectId: string, userId: string, seedText: string, placeholderName: string, history?: Array<{ role: string; content: string }>) => Promise<void>;
    getDisplayHistory: () => Array<{
        sender: 'user' | 'bark';
        message: string;
        activityLog?: Array<ActivityLogEntry>;
        thinking?: string;
        messageId?: string;
        feedbackProvided?: boolean;
    }>;
}

// =============================================================================
// STORE
// =============================================================================

const AUTOSAVE_DELAY_MS = 500;  // Reduced from 2000ms to minimize race condition window

function createEmptyPlanWorkspace(projectId: string): DictWorkspaceState {
    return {
        workspace_id: `ws_${projectId}`,
        sequence_number: 0,
        compartment_id: 'default',
        findings: {},
        nodes: {},
        edges: {},
        markers: {},
        reports: {},
        clusters: {},
        models: {},
        doc_files: {},
        output_package_meta: null,
        online_support: {},
        uploaded_files: {},
        project: null,
        derived_at: new Date().toISOString(),
    };
}

// Recency cap for the active project list: the Project Library shows your
// ACTIVE_PROJECT_CAP most-recent owned projects as "active"; everything older
// (the recency overflow) surfaces under the Archived tab alongside any projects
// you manually archived. Keep this in sync between the active fetch size and the
// overflow slice in loadArchivedProjects.
export const ACTIVE_PROJECT_CAP = 50;

// Fetch every page for a status from `startPage` (page_size caps at 100
// backend-side). The first page reveals total_count, then any remaining pages
// are fetched IN PARALLEL rather than one-by-one — so the Archived tab waits on
// ~2 round-trips, not N. Bounded at 20 pages as a runaway guard. For the recency
// overflow we start past the active cap to skip the already-loaded first page.
async function fetchProjectsPaginated(
    userId: string,
    status: 'active' | 'archived_visible',
    pageSize: number,
    startPage: number = 1,
): Promise<ProjectSummary[]> {
    const first = await apiListProjects(userId, startPage, pageSize, status, true);
    if (!first.success || !first.data) return [];
    const all: ProjectSummary[] = [...first.data.projects];
    // Done if this page wasn't full or we've already covered the total.
    const coveredSoFar = (startPage - 1) * pageSize + first.data.projects.length;
    if (first.data.projects.length < pageSize || coveredSoFar >= first.data.total_count) {
        return all;
    }
    const lastPage = Math.min(Math.ceil(first.data.total_count / pageSize), startPage + 19);
    const remaining: number[] = [];
    for (let page = startPage + 1; page <= lastPage; page++) remaining.push(page);
    // allSettled (not all): a transient failure on one page must not throw away
    // the pages that did load. apiListProjects already resolves to {success:false}
    // rather than rejecting, but allSettled keeps this robust regardless.
    const results = await Promise.allSettled(
        remaining.map((page) => apiListProjects(userId, page, pageSize, status, true)),
    );
    for (const r of results) {
        if (r.status === 'fulfilled' && r.value.success && r.value.data) {
            all.push(...r.value.data.projects);
        } else {
            // A dropped page means the archived list is missing a chunk — log it
            // so a data gap is at least visible to operators.
            console.warn('[ProjectStore] archived paging: dropped a page for status', status);
        }
    }
    return all;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
    // BAR-523: first-turn upload stash (see the interface for the invariant).
    firstTurnUploads: null,
    setFirstTurnUploads: (value) => set({ firstTurnUploads: value }),
    consumeFirstTurnUploads: (projectId) => {
        const stashed = get().firstTurnUploads;
        if (!stashed || stashed.projectId !== projectId) return [];
        set({ firstTurnUploads: null });
        return stashed.files;
    },

    // Initial state
    activeProject: null,
    projectList: [],
    projectListUserId: null,
    archivedProjectList: [],
    archivedLoaded: false,
    archivedLoading: false,
    archivedVisibleCount: 0,
    totalProjects: 0,
    syncStatus: 'idle',
    syncError: null,
    lastSyncedAt: null,
    pendingChanges: false,
    isLoading: false,
    listLoading: false,
    listFailedFor: null,
    workspaceLoading: false,

    // Dict workspace initial state (event-sourced from Report Library)
    dictWorkspace: null,
    requirementsRefreshSignal: 0,

    // Firestore events initial state
    firestoreEvents: [],
    lastEventSequence: 0,
    lastPersistedSequence: 0,
    eventsLoading: false,
    oldestLoadedSequence: 0,
    earlierEventsLoading: false,

    // Multi-workspace initial state
    activeWorkspace: 'report',
    workspaceMode: 'research',
    barkWorkspace: 'research',
    barkWorkspaceName: '',
    workspaceSyncStatus: { ...DEFAULT_WORKSPACE_SYNC_STATUS },
    pendingWorkspaceSwitch: null,
    productionState: { ...DEFAULT_PRODUCTION_STATE },

    // Task & Institution Context initial state
    plan: { ...DEFAULT_PLAN_STATE },
    institutionProfile: { ...DEFAULT_INSTITUTION_PROFILE },

    _currentUserId: null,
    _saveTimeoutId: null,
    _currentVersion: 0,
    _changeRevision: 0,
    _projectEpoch: 0,
    _savePromise: null,
    _saveProjectId: null,

    // Actions
    createProject: async (userId, name, description = '', projectType?, options?) => {
        const stateBeforeCreate = get();
        const projectBeforeCreate = stateBeforeCreate.activeProject?.project_id ?? null;
        if (
            stateBeforeCreate.activeProject
            && stateBeforeCreate.pendingChanges
            && !(await get().saveWorkspaceNow(stateBeforeCreate.activeProject.user_id))
        ) {
            return null;
        }
        if (
            get()._projectEpoch !== stateBeforeCreate._projectEpoch
            || (get().activeProject?.project_id ?? null) !== projectBeforeCreate
        ) {
            return null;
        }

        // Clear all previous project state BEFORE creating new project.
        // This prevents data leakage between projects.
        // Build projects start at 'plan' phase; research-only starts at 'research'.
        //
        // `options.skipReset` is set by callers that have already prepared
        // the store for this create (e.g. App.tsx's lazy-create from Build
        // draft mode, where activeProject/dictWorkspace are already null
        // and workspaceMode/activeWorkspace are already where we want them
        // — running the wipe-and-reset would emit an intermediate render
        // that briefly mismatches the in-flight UI state).
        const initialMode: WorkspaceMode = projectType === 'build' ? 'plan' : 'research';
        const createEpoch = get()._projectEpoch + 1;
        if (options?.skipReset) {
            set({ isLoading: true, _projectEpoch: createEpoch });
        } else {
            set({
                isLoading: true,
                activeProject: null,      // Clear old project to prevent stale project_id in messages
                dictWorkspace: null,      // Clear old workspace data to prevent showing previous project's nodes
                firestoreEvents: [],
                lastEventSequence: 0,
                lastPersistedSequence: 0,
                eventsLoading: false,
                oldestLoadedSequence: 0,
                earlierEventsLoading: false,
                activeWorkspace: 'report',
                workspaceMode: initialMode,
                workspaceSyncStatus: { ...DEFAULT_WORKSPACE_SYNC_STATUS },
                pendingWorkspaceSwitch: null,
                productionState: { ...DEFAULT_PRODUCTION_STATE },
                plan: { ...DEFAULT_PLAN_STATE },
                institutionProfile: { ...DEFAULT_INSTITUTION_PROFILE },
                _changeRevision: 0,
                _projectEpoch: createEpoch,
                _savePromise: null,
                _saveProjectId: null,
            });
        }

        const result = await apiCreateProject(userId, name, description, [], projectType);

        if (get()._projectEpoch !== createEpoch) {
            console.info('[ProjectStore] Ignoring create completion for an abandoned project draft');
            return null;
        }

        if (result.success && result.project) {
            const createdProjectType = result.project.project_type || projectType;
            set({
                activeProject: result.project,
                _currentVersion: result.project.version,
                syncStatus: 'synced',
                lastSyncedAt: new Date(),
                isLoading: false,
                pendingChanges: false,
                _changeRevision: 0,
                ...(createdProjectType === 'build' ? {
                    dictWorkspace: createEmptyPlanWorkspace(result.project.project_id),
                    plan: { ...DEFAULT_PLAN_STATE },
                    institutionProfile: { ...DEFAULT_INSTITUTION_PROFILE },
                } : {}),
            });
            return result.project;
        }

        set({ isLoading: false, syncError: result.error || 'Failed to create project' });
        return null;
    },

    loadProject: async (projectId, userId, opts) => {
        const stateBeforeLoad = get();
        if (
            stateBeforeLoad.activeProject
            && stateBeforeLoad.activeProject.project_id !== projectId
        ) {
            // Project switches must clear the current session first. Rejecting
            // a stale reload here also avoids clearing the live project's
            // workspace and events before the first network request.
            return false;
        }
        if (
            stateBeforeLoad.activeProject
            && stateBeforeLoad.pendingChanges
            && !opts?.discardPendingChanges
            && !(await get().saveWorkspaceNow(stateBeforeLoad.activeProject.user_id))
        ) {
            return false;
        }
        if (
            get()._projectEpoch !== stateBeforeLoad._projectEpoch
            || (get().activeProject?.project_id ?? null)
                !== (stateBeforeLoad.activeProject?.project_id ?? null)
        ) {
            return false;
        }

        // Preserve the user's current viewport across mid-session reloads
        // (e.g. when the agent finishes a turn and the project state
        // refreshes). The first open of a project — when the store still
        // holds no activeProject, or when the previously-active project
        // had a different id — is the only time we route the user to
        // the canonical landing workspace ('plan' for build projects,
        // 'research' for research projects). Subsequent reloads keep
        // the user wherever they were.
        const prevState = get();
        const isReloadOfSameProject = (
            prevState.activeProject?.project_id === projectId
        );
        const preserveViewport = opts?.preserveViewport ?? isReloadOfSameProject;
        const previousWorkspaceMode = prevState.workspaceMode;
        // The plan-level state lives under `ws_${projectId}`; spawned
        // workspaces (one per plan node) have their own ids. On a
        // mid-session reload, the dictWorkspace below gets re-hydrated
        // from the plan workspace — so when the user was viewing a
        // spawned workspace, we re-load it after the plan load
        // completes. Without this, ProcessViewer mounts with the plan
        // workspace_id and renders empty until the user re-clicks the
        // sidebar.
        const previousDictWorkspaceId = prevState.dictWorkspace?.workspace_id ?? null;
        const loadEpoch = prevState._projectEpoch + 1;
        set({
            isLoading: true,
            _currentUserId: userId,
            _changeRevision: 0,
            _projectEpoch: loadEpoch,
            _savePromise: null,
            _saveProjectId: null,
        });
        console.log('[ProjectStore] Loading project:', projectId);
        const result = await apiGetProject(projectId, userId);

        const stateAfterProjectRequest = get();
        if (stateAfterProjectRequest._projectEpoch !== loadEpoch) {
            console.info('[ProjectStore] Ignoring stale load completion for', projectId);
            return false;
        }
        if (stateAfterProjectRequest._changeRevision !== 0) {
            // A local mutation landed after this read began. Applying the
            // fetched snapshot would erase that newer edit, so preserve the
            // local state and let its autosave complete instead.
            set({ isLoading: false });
            console.info('[ProjectStore] Ignoring load completion after a newer local edit for', projectId);
            return false;
        }

        if (result.success && result.project) {
            // Initialize production state with artifacts from project (if any)
            const projectArtifacts = (result.project as any).artifacts || [];
            const firstDoc = projectArtifacts.find((a: any) => a.type === 'doc');
            const firstSheet = projectArtifacts.find((a: any) => a.type === 'sheet');

            // Restore active workspace from project (default to 'report' for legacy projects)
            // Legacy 'search' workspace was removed; reroute to 'report'.
            const savedWorkspace = (result.project as any).active_workspace as string | undefined;
            const activeWorkspace: WorkspaceType = (savedWorkspace === 'report' || savedWorkspace === 'graph')
                ? savedWorkspace : 'report';
            // Build projects: start in plan workspace on first open.
            // Research projects: start in research workspace.
            // On mid-session reload (preserveViewport=true), keep
            // whatever workspace the user was already in — bumping
            // them back to plan after every agent turn is jarring.
            const projectType = result.project?.project_type || 'research';
            const landingMode: WorkspaceMode = projectType === 'build' ? 'plan' : 'research';
            const workspaceMode: WorkspaceMode = preserveViewport
                ? previousWorkspaceMode
                : landingMode;
            console.log(`[ProjectStore] Starting in ${workspaceMode} workspace (project_type=${projectType}, saved was: ${savedWorkspace}, preserveViewport=${preserveViewport})`);

            // BAR-53: a DIFFERENT project became active during the apiGetProject
            // await — bail so this stale load can't overwrite it. NOTE the
            // `live1 &&` truthy check is deliberate HERE (unlike the post-set
            // guards below): loadProject assigns activeProject a few lines down,
            // so during a normal handleSelectProject load — which calls
            // clearActiveProject() first — live1 is legitimately null at this
            // point and must NOT bail. We also intentionally do NOT clear
            // isLoading on bail: that flag now belongs to the newer in-flight
            // load, and clearing it would drop the new project's opening overlay.
            const live1 = get();
            if (
                live1._projectEpoch !== loadEpoch
                || (live1.activeProject && live1.activeProject.project_id !== projectId)
            ) {
                console.log(
                    '[ProjectStore] Discarding stale loadProject (post-apiGetProject) for',
                    projectId,
                    '— current:',
                    live1.activeProject?.project_id,
                );
                return false;
            }

            set({
                activeProject: result.project,
                _currentVersion: result.project.version,
                syncStatus: 'synced',
                lastSyncedAt: new Date(result.project.updated_at),
                isLoading: false,
                pendingChanges: false,
                _changeRevision: 0,
                activeWorkspace,  // Restore persisted workspace tab
                workspaceMode,    // Restore persisted workspace mode
                barkWorkspace: ((result.project as any).bark_workspace as WorkspaceMode) || workspaceMode,  // BARK's persisted workspace from project
                productionState: {
                    ...DEFAULT_PRODUCTION_STATE,
                    artifacts: projectArtifacts,
                    activeArtifactId: projectArtifacts[0]?.artifact_id || null,
                    googleDocUrl: firstDoc?.url || null,
                    googleSheetUrl: firstSheet?.url || null,
                },
            });

            // Load workspace state from Report Library (event-sourced, single source of truth)
            console.log('[ProjectStore] Loading workspace state from Report Library...');
            const workspaceId = `ws_${projectId}`;
            const workspaceChangeRevision = get()._changeRevision;
            const workspaceResult = await apiGetWorkspaceState(workspaceId, userId);
            if (workspaceResult.success && workspaceResult.state) {
                // Hydrate plan state from workspace if present
                const ws = workspaceResult.state;
                const planSections = ws.plan_sections || {};
                const planItems = ws.plan_items || {};
                const planDocs = ws.plan_documents || {};
                const hasPlan = ws.plan_objective || Object.keys(planSections).length > 0 || Object.keys(planItems).length > 0;

                let hydratedPlan: typeof DEFAULT_PLAN_STATE | undefined;
                if (hasPlan) {
                    // Convert flat dicts to nested PlanSection[] with items
                    const sectionsArr = Object.values(planSections)
                        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                        .map((s: any) => ({
                            section_id: s.section_id,
                            title: s.title,
                            target_phase: s.target_phase,
                            order: s.order ?? 0,
                            items: Object.values(planItems)
                                .filter((it: any) => it.section_id === s.section_id)
                                .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                                .map((it: any) => ({
                                    item_id: it.item_id,
                                    section_id: it.section_id,
                                    text: it.text,
                                    status: it.status || 'planned',
                                    revision_note: it.revision_note,
                                    phase_origin: it.phase_origin,
                                    order: it.order ?? 0,
                                })),
                        }));
                    const docsArr = Object.values(planDocs).map((d: any) => ({
                        document_id: d.document_id,
                        filename: d.filename,
                        summary: d.summary || '',
                        status: d.status || 'pending',
                    }));
                    hydratedPlan = {
                        objective: ws.plan_objective || '',
                        sections: sectionsArr,
                        documents: docsArr,
                        is_ready: ws.plan_ready || false,
                    };
                    console.log('[ProjectStore] Hydrated plan from workspace:', {
                        objective: !!ws.plan_objective,
                        sections: sectionsArr.length,
                        items: Object.keys(planItems).length,
                    });
                }

                // Hydrate institution profile from workspace if present
                const hydratedInstitution = hydrateInstitutionProfileFromState(ws);
                if (hydratedInstitution) {
                    console.log('[ProjectStore] Hydrated institution profile from workspace:', {
                        fields: Object.keys(ws.profile_fields || {}).length,
                        choices: Object.keys(ws.regulatory_choices || {}).length,
                    });
                }

                // Post-set guard: activeProject was assigned projectId above, so a
                // null here means the user left (clearActiveProject) mid-fetch —
                // bail on that too, hence `!== projectId` rather than `live2 &&`.
                const live2 = get();
                if (
                    live2._projectEpoch !== loadEpoch
                    || live2.activeProject?.project_id !== projectId
                    || live2._changeRevision !== workspaceChangeRevision
                ) {
                    console.log(
                        '[ProjectStore] Discarding stale loadProject (workspace) for',
                        projectId,
                        '— current:',
                        live2.activeProject?.project_id,
                    );
                    return false;
                }

                set({
                    dictWorkspace: buildDictWorkspaceStateFromResponse(ws),
                    ...(hydratedPlan ? { plan: hydratedPlan } : {}),
                    ...(hydratedInstitution ? { institutionProfile: hydratedInstitution } : {}),
                });
                console.log('[ProjectStore] Loaded workspace state:', {
                    nodes: Object.keys(ws.nodes).length,
                    findings: Object.keys(ws.findings).length,
                    sequence: ws.sequence_number,
                });
            }

            // Re-hydrate the spawned workspace the user was viewing
            // before the reload. The plan-state load above clobbers
            // dictWorkspace with the plan workspace; on mid-session
            // reloads we want the user's original viewport back so
            // ProcessViewer / the production graph render without a
            // manual re-click.
            const planWorkspaceId = `ws_${projectId}`;
            if (
                preserveViewport &&
                previousDictWorkspaceId &&
                previousDictWorkspaceId !== planWorkspaceId
            ) {
                console.log('[ProjectStore] Re-hydrating spawned workspace after reload:', previousDictWorkspaceId);
                // No workspace-loading overlay here: the project-open overlay
                // (isProjectOpening) already covers this window.
                const rehydrated = await get().loadSpecificWorkspace(previousDictWorkspaceId, userId, {
                    showLoading: false,
                    expectedProjectEpoch: loadEpoch,
                    expectedChangeRevision: get()._changeRevision,
                });
                if (!rehydrated || get()._projectEpoch !== loadEpoch) {
                    return false;
                }
            }

            // Load events from Firestore subcollection (pure event-based architecture)
            console.log('[ProjectStore] Loading events from Firestore subcollection...');
            const eventsAtRequest = get().firestoreEvents;
            const eventsChangeRevision = get()._changeRevision;
            // Newest page only. A cold open has no watermark, so this used to
            // ask for `since_sequence=0` and read the ENTIRE log — on prod's
            // largest project 3,920 events and 2.9 MiB in one request, 72s
            // under load, to fill a pane showing the last few turns. Older
            // turns arrive from `loadEarlierEvents` when the user asks.
            const eventsResult = await getProjectEventsFirestore(projectId, userId, 0, {
                tail: true,
                limit: EVENT_PAGE_SIZE,
            });
            if (eventsResult.success && eventsResult.events) {
                // `latest_sequence` is the newest event in the WHOLE log (the
                // page is the tail), so it is the correct watermark even
                // though we hold only part of the history.
                const lastSeq = eventsResult.latestSequence
                    ?? (eventsResult.events.length > 0
                        ? Math.max(...eventsResult.events.map(e => e.sequence))
                        : 0);
                // Post-set guard (see live2 above): null here = user left mid-fetch.
                const live3 = get();
                if (
                    live3._projectEpoch !== loadEpoch
                    || live3.activeProject?.project_id !== projectId
                ) {
                    console.log(
                        '[ProjectStore] Discarding stale loadProject (events) for',
                        projectId,
                        '— current:',
                        live3.activeProject?.project_id,
                    );
                    return false;
                }
                if (
                    live3._changeRevision !== eventsChangeRevision
                    || live3.firestoreEvents !== eventsAtRequest
                ) {
                    // Same project, same epoch, but the event log moved while
                    // this fetch was in flight — a local append, or (the common
                    // case on a first open) the collab-subscription catchup
                    // replaying a historical `complete` and reloading this very
                    // project's events. That newer state must not be clobbered
                    // by our page (it carries its own watermarks), but it is NOT
                    // a reason to fail the open: the project is loaded and
                    // hydrated. Keep the newer events and complete the load.
                    // (Returning false here silently dropped the opening overlay
                    // and never navigated — the "clicked a project, it stopped
                    // loading, nothing shown" report on shared projects, whose
                    // slower reads widened this window.)
                    console.info(
                        '[ProjectStore] Events moved during loadProject for',
                        projectId,
                        '— keeping the newer event log, load continues',
                    );
                } else {
                    set({
                        firestoreEvents: eventsResult.events,
                        lastEventSequence: lastSeq,
                        lastPersistedSequence: lastSeq,
                        oldestLoadedSequence: eventsResult.oldestSequence ?? 0,
                    });
                    console.log(
                        '[ProjectStore] Loaded', eventsResult.events.length,
                        'events from Firestore (newest page, oldest seq',
                        eventsResult.oldestSequence, 'of latest', lastSeq, ')',
                    );
                }
            }

            // Load per-user workspace preference (for future use, but don't
            // override initial plan view — user always starts at project overview)
            const prefsResult = await getUserPrefs(projectId, userId);
            if (prefsResult.success && prefsResult.prefs?.workspace_mode) {
                console.log('[ProjectStore] User had saved workspace preference:', prefsResult.prefs.workspace_mode, '(not applied — always start in plan)');
            }

            return true;
        }

        console.warn('[ProjectStore] Failed to load project:', result.error);
        set({
            isLoading: false,
            syncStatus: 'error',
            syncError: result.error || 'Failed to load project',
        });
        return false;
    },

    loadProjectList: async (userId, page = 1, append = false, pageSize = ACTIVE_PROJECT_CAP, light = false) => {
        // The active list changing means the recency overflow may have shifted,
        // so invalidate the Archived cache — the next time that tab opens it
        // refetches instead of showing a stale overflow.
        set({ listLoading: true, archivedLoaded: false, listFailedFor: null });
        let result: Awaited<ReturnType<typeof apiListProjects>>;
        try {
            result = await apiListProjects(userId, page, pageSize, 'active', light);
        } catch {
            set({ listLoading: false, listFailedFor: userId });
            return false;
        }

        if (result.success && result.data) {
            const state = get();
            set({
                projectList: append
                    ? [...state.projectList, ...result.data.projects]
                    : result.data.projects,
                projectListUserId: userId,
                totalProjects: result.data.total_count,
                listLoading: false,
            });
            return true;
        }

        set({ listLoading: false, listFailedFor: userId });
        return false;
    },

    updateWorkspace: (workspace, uiState) => {
        const state = get();
        if (!state.activeProject) return;

        // Clear any existing timeout
        if (state._saveTimeoutId) {
            clearTimeout(state._saveTimeoutId);
        }

        // Update local state immediately
        set({
            activeProject: {
                ...state.activeProject,
                workspace,
                workspace_ui_state: uiState || state.activeProject.workspace_ui_state,
            },
            pendingChanges: true,
            syncStatus: 'pending',
            _changeRevision: state._changeRevision + 1,
        });

        // Schedule auto-save
        const timeoutId = setTimeout(() => {
            const currentState = get();
            if (currentState.activeProject && currentState.pendingChanges) {
                // We need userId - get it from the active project
                const userId = currentState.activeProject.user_id;
                get().saveWorkspaceNow(userId);
            }
        }, AUTOSAVE_DELAY_MS);

        set({ _saveTimeoutId: timeoutId });
    },

    updateReferenceMaterials: (referenceMaterials) => {
        const state = get();
        if (!state.activeProject) return;

        // Clear any existing timeout
        if (state._saveTimeoutId) {
            clearTimeout(state._saveTimeoutId);
        }

        // Update local state immediately
        set({
            activeProject: {
                ...state.activeProject,
                reference_materials: referenceMaterials,
            },
            pendingChanges: true,
            syncStatus: 'pending',
            _changeRevision: state._changeRevision + 1,
        });

        // Schedule auto-save
        const timeoutId = setTimeout(() => {
            const currentState = get();
            if (currentState.activeProject && currentState.pendingChanges) {
                const userId = currentState.activeProject.user_id;
                get().saveWorkspaceNow(userId);
            }
        }, AUTOSAVE_DELAY_MS);

        set({ _saveTimeoutId: timeoutId });
    },

    saveWorkspaceNow: async (userId) => {
        const initialState = get();
        if (!initialState.activeProject || !initialState.pendingChanges) {
            return true;
        }

        const projectId = initialState.activeProject.project_id;
        const projectEpoch = initialState._projectEpoch;

        // All callers for the same project session join one drain promise.
        // Display status is deliberately not the lock: a new edit changes the
        // visible status to `pending` while the request is still in flight.
        if (
            initialState._savePromise
            && initialState._saveProjectId === projectId
        ) {
            return initialState._savePromise;
        }

        const savePromise = (async (): Promise<boolean> => {
            while (true) {
                const state = get();
                if (
                    !state.activeProject
                    || state.activeProject.project_id !== projectId
                    || state._projectEpoch !== projectEpoch
                ) {
                    // The response belongs to a project session the user has
                    // already left. Never let it rewrite the new session.
                    return true;
                }
                if (!state.pendingChanges) {
                    return true;
                }

                if (state._saveTimeoutId) {
                    clearTimeout(state._saveTimeoutId);
                }

                const sentRevision = state._changeRevision;
                set({ syncStatus: 'syncing', _saveTimeoutId: null });

                const result = await apiUpdateWorkspace(
                    projectId,
                    userId,
                    state.activeProject.workspace,
                    state.activeProject.findings || null,
                    state.activeProject.workspace_ui_state,
                    state.activeProject.reference_materials,
                    state.activeProject.draft_report || null,
                    null,  // Chat persistence handled by backend ChatPersistenceManager
                    state.activeProject.description || null,
                    state._currentVersion,
                    (state.activeProject as any).artifacts || state.productionState.artifacts || null,
                    state.workspaceMode  // Workspace mode: 'research' or 'production'
                );

                const current = get();
                if (
                    !current.activeProject
                    || current.activeProject.project_id !== projectId
                    || current._projectEpoch !== projectEpoch
                ) {
                    console.info('[ProjectStore] Ignoring save completion for inactive project', projectId);
                    return true;
                }

                // Check the body before the generic success path. This protects
                // users even if a legacy server or intermediary emits HTTP 200.
                if (result.data?.conflict) {
                    set({
                        syncStatus: 'error',
                        syncError: 'Version conflict — reload the project before editing further',
                        pendingChanges: true,
                    });
                    return false;
                }

                if (
                    result.success
                    && result.data
                    && typeof result.data.new_version === 'number'
                    && typeof result.data.updated_at === 'string'
                ) {
                    const hasNewerChanges = current._changeRevision !== sentRevision;
                    set({
                        _currentVersion: result.data.new_version,
                        syncStatus: hasNewerChanges ? 'pending' : 'synced',
                        lastSyncedAt: new Date(result.data.updated_at),
                        pendingChanges: hasNewerChanges,
                        syncError: null,
                    });

                    // A mutation landed while the request was in flight. Loop
                    // immediately using the version returned by this save.
                    if (hasNewerChanges) {
                        continue;
                    }
                    return true;
                }

                set({
                    syncStatus: 'error',
                    syncError: result.error || 'Failed to save workspace',
                    pendingChanges: true,
                });
                return false;
            }
        })();

        set({ _savePromise: savePromise, _saveProjectId: projectId });
        try {
            return await savePromise;
        } finally {
            if (get()._savePromise === savePromise) {
                set({ _savePromise: null, _saveProjectId: null });
            }
        }
    },

    archiveProject: async (projectId, userId) => {
        const activeBeforeArchive = get().activeProject;
        if (
            activeBeforeArchive?.project_id === projectId
            && get().pendingChanges
            && !(await get().saveWorkspaceNow(activeBeforeArchive.user_id))
        ) {
            return false;
        }

        const result = await apiArchiveProject(projectId, userId);

        if (result.success) {
            const state = get();
            // Remove from list
            set({
                projectList: state.projectList.filter(p => p.project_id !== projectId),
                totalProjects: state.totalProjects - 1,
            });
            // Clear if it's the active project
            if (get().activeProject?.project_id === projectId) {
                await get().clearActiveProject({ discardPendingChanges: true });
            }
            return true;
        }
        return false;
    },

    loadArchivedProjects: async (userId) => {
        // Backs the "Archived" tab, which is the UNION of two things:
        //   1. manually archived projects (status=archived_visible), and
        //   2. the recency overflow — active projects beyond the most-recent
        //      ACTIVE_PROJECT_CAP (these keep status='active', they're just old).
        // The hidden 'archived' soft-delete state (the Delete button) is never
        // surfaced here.
        // Paginate (page_size caps at 100). For the overflow, page the active
        // list at ACTIVE_PROJECT_CAP per page and START AT PAGE 2 — so page 1
        // (the active 50 already loaded for "Mine") is skipped, not re-fetched.
        // If there's no overflow, page 2 returns empty and we stop after one call.
        set({ archivedLoading: true });
        try {
            const [manual, overflow] = await Promise.all([
                fetchProjectsPaginated(userId, 'archived_visible', 100),
                fetchProjectsPaginated(userId, 'active', ACTIVE_PROJECT_CAP, 2),
            ]);
            // Reconcile the badge count with the freshly-loaded list so the two
            // can't disagree once the tab has been opened.
            set({ archivedProjectList: [...overflow, ...manual], archivedLoaded: true, archivedVisibleCount: manual.length });
            return true;
        } catch (err) {
            // Mark loaded even on failure so the view's effect doesn't retry-loop
            // against a failing endpoint. The cache invalidates on the next active
            // list reload (any mutation), which lets it try again.
            console.warn('[ProjectStore] loadArchivedProjects failed:', err);
            set({ archivedLoaded: true });
            return false;
        } finally {
            // Always clear the spinner — never leave it stuck on.
            set({ archivedLoading: false });
        }
    },

    loadArchivedCount: async (userId) => {
        // Cheap count for the Archived badge. page_size=1 means the backend
        // returns total_count (a count() aggregation — it does NOT stream the
        // projects) while downloading a single light document. So the badge can
        // show a number on page load without paying for the full Archived list,
        // which stays lazy and only loads when the tab is opened.
        const res = await apiListProjects(userId, 1, 1, 'archived_visible', true);
        if (res.success && res.data) set({ archivedVisibleCount: res.data.total_count });
    },

    shelveProject: async (projectId, userId) => {
        const activeBeforeShelve = get().activeProject;
        if (
            activeBeforeShelve?.project_id === projectId
            && get().pendingChanges
            && !(await get().saveWorkspaceNow(activeBeforeShelve.user_id))
        ) {
            return false;
        }

        const result = await apiArchiveToFolder(projectId, userId);
        if (!result.success) return false;
        const state = get();
        // Drop it from the active list immediately; clear it if it's open.
        // Invalidate the archived cache here too (not only via the caller's
        // loadProjectList) so a direct shelveProject call can't leave the
        // Archived tab stale.
        set({
            projectList: state.projectList.filter(p => p.project_id !== projectId),
            totalProjects: Math.max(0, state.totalProjects - 1),
            archivedLoaded: false,
        });
        if (get().activeProject?.project_id === projectId) {
            await get().clearActiveProject({ discardPendingChanges: true });
        }
        return true;
    },

    unshelveProject: async (projectId, userId) => {
        const result = await apiUnarchiveProject(projectId, userId);
        if (!result.success) return false;
        // Drop it from the archived list AND invalidate the cache (like
        // shelveProject) — restoring shifts the overflow boundary, so the
        // Archived tab must refetch. Doesn't rely on the caller's loadProjectList.
        set({
            archivedProjectList: get().archivedProjectList.filter(p => p.project_id !== projectId),
            archivedLoaded: false,
        });
        return true;
    },

    duplicateProject: async (projectId: string, userId: string): Promise<{ success: boolean; new_project_id?: string; error?: string; warnings?: any; skipped_non_canonical_notebooks?: string[] }> => {
        const { duplicateProject: apiDuplicateProject, getDuplicateJob } = await import('../apiService');

        const startResult = await apiDuplicateProject(projectId, userId);
        if (!startResult.success || !startResult.job_id) {
            return { success: false, error: startResult.error || 'Failed to start duplicate' };
        }

        const jobId = startResult.job_id;
        const newProjectId = startResult.new_project_id;

        // Mark this row as in-progress in the store.
        const state = get();
        set({
            duplicateJobs: { ...(state.duplicateJobs || {}), [projectId]: { jobId, newProjectId, phase: 'pending' } },
        });

        // Poll every 2s for up to 10 minutes.
        const POLL_INTERVAL_MS = 2000;
        const MAX_POLLS = 300;
        for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
            const pollResult = await getDuplicateJob(jobId, userId);
            if (!pollResult.success || !pollResult.job) {
                continue;
            }
            const job = pollResult.job;

            // Update phase in store
            const s = get();
            set({
                duplicateJobs: {
                    ...(s.duplicateJobs || {}),
                    [projectId]: { jobId, newProjectId: job.new_project_id, phase: job.phase || job.status },
                },
            });

            if (job.status === 'completed') {
                // Refresh project list and mark new project for inline rename.
                await get().loadProjectList(userId);
                const cleared = { ...(get().duplicateJobs || {}) };
                delete cleared[projectId];
                set({
                    duplicateJobs: cleared,
                    pendingRenameProjectId: job.new_project_id,
                });
                return {
                    success: true,
                    new_project_id: job.new_project_id,
                    warnings: job.notebook_paths_with_old_id_references,
                    skipped_non_canonical_notebooks: job.notebook_paths_skipped_non_canonical,
                };
            }
            if (job.status === 'failed' || job.status === 'failed_corrupt') {
                const cleared = { ...(get().duplicateJobs || {}) };
                delete cleared[projectId];
                set({ duplicateJobs: cleared });
                return { success: false, error: job.error || 'Duplicate failed' };
            }
        }

        // Timed out polling.
        const cleared = { ...(get().duplicateJobs || {}) };
        delete cleared[projectId];
        set({ duplicateJobs: cleared });
        return { success: false, error: 'Duplicate timed out (10 minutes)' };
    },

    clearPendingRename: () => set({ pendingRenameProjectId: null }),

    clearActiveProject: async (options) => {
        const state = get();
        const projectId = state.activeProject?.project_id ?? null;
        const projectEpoch = state._projectEpoch;

        if (
            state.activeProject
            && state.pendingChanges
            && !options?.discardPendingChanges
        ) {
            const saved = await get().saveWorkspaceNow(state.activeProject.user_id);
            if (!saved) {
                // Keep the project and its actionable sync error visible. The
                // caller must not complete a project transition after false.
                return false;
            }

            const current = get();
            if (
                current.activeProject?.project_id !== projectId
                || current._projectEpoch !== projectEpoch
            ) {
                // A different transition completed while this save drained.
                // Never clear the newer project session.
                return true;
            }
        }

        const currentState = get();
        if (currentState._saveTimeoutId) {
            clearTimeout(currentState._saveTimeoutId);
        }
        set((current) => ({
            activeProject: null,
            isLoading: false,
            syncStatus: 'idle',
            syncError: null,
            pendingChanges: false,
            _currentUserId: null,
            _saveTimeoutId: null,
            _currentVersion: 0,
            _changeRevision: 0,
            _projectEpoch: current._projectEpoch + 1,
            _savePromise: null,
            _saveProjectId: null,
            // Reset dict workspace state
            dictWorkspace: null,
            // Reset multi-workspace state
            activeWorkspace: 'report',
            workspaceMode: 'research',
            barkWorkspace: 'research',
            barkWorkspaceName: '',
            workspaceSyncStatus: { ...DEFAULT_WORKSPACE_SYNC_STATUS },
            pendingWorkspaceSwitch: null,
            productionState: { ...DEFAULT_PRODUCTION_STATE },
            plan: { ...DEFAULT_PLAN_STATE },
            institutionProfile: { ...DEFAULT_INSTITUTION_PROFILE },
            // Reset Firestore events state
            firestoreEvents: [],
            lastEventSequence: 0,
            lastPersistedSequence: 0,
            eventsLoading: false,
            oldestLoadedSequence: 0,
            earlierEventsLoading: false,
        }));
        return true;
    },

    setSyncStatus: (status, error) => {
        set({ syncStatus: status, syncError: error || null });
    },

    // Multi-workspace actions
    setActiveWorkspace: (workspace) => {
        const state = get();

        // Save pending changes before switching
        if (state.pendingChanges && state.activeProject) {
            const userId = state.activeProject.user_id;
            get().saveWorkspaceNow(userId);
        }

        set({
            activeWorkspace: workspace,
            pendingWorkspaceSwitch: null, // Clear any pending switch proposal
            pendingChanges: true,  // Mark as changed to trigger save
            _changeRevision: state._changeRevision + 1,
        });

        console.log('[ProjectStore] Switched to workspace:', workspace);

        // Trigger save to persist workspace type
        if (state.activeProject) {
            const userId = state.activeProject.user_id;
            get().saveWorkspaceNow(userId);
        }
    },

    setWorkspaceMode: (mode, initiator) => {
        const previousMode = get().workspaceMode;
        set((state) => ({
            workspaceMode: mode,
            pendingChanges: true,
            _changeRevision: state._changeRevision + 1,
        }));
        console.log('[ProjectStore] Workspace mode set to:', mode, 'by:', initiator || 'system');
        const project = get().activeProject;
        if (project) {
            get().saveWorkspaceNow(project.user_id);
        }
        // Persist user's workspace preference (per-user, per-project)
        const currentUserId = get()._currentUserId;
        if (initiator === 'user' && project && currentUserId) {
            setUserPrefs(project.project_id, currentUserId, { workspace_mode: mode })
                .catch(() => {}); // Non-critical
        }
        // Only BARK/agent workspace switches are persisted to event history.
        // User tab clicks are local-only — they don't appear in chat/events.
        if (initiator === 'agent' && project && mode !== previousMode) {
            const content = JSON.stringify({ from: previousMode, to: mode, initiator });
            const eventTimestamp = new Date().toISOString();
            get().addLocalEvent({
                project_id: project.project_id,
                type: 'workspace_switch',
                content,
                timestamp: eventTimestamp,
            });
            // Persist to Firestore with the same client timestamp so it sorts correctly
            // among orchestrator events after a page reload
            persistEvent(project.project_id, project.user_id, 'workspace_switch', content, undefined, eventTimestamp)
                .then(() => console.log('[ProjectStore] workspace_switch event persisted'))
                .catch((err) => console.warn('[ProjectStore] Failed to persist workspace_switch:', err));
        }
    },

    setBarkWorkspace: (mode, expectedProjectId) => {
        // BAR-53 (F5): drop a stale workspace-indicator switch tagged for a
        // project the user has switched away from (e.g. a late collab /
        // subscription event on a not-yet-closed socket). No-op when
        // expectedProjectId is omitted (preserves any non-event caller).
        if (expectedProjectId && get().activeProject?.project_id !== expectedProjectId) {
            console.log('[ProjectStore] Dropping stale setBarkWorkspace for', expectedProjectId, '— current:', get().activeProject?.project_id);
            return;
        }
        set({ barkWorkspace: mode });
        console.log('[ProjectStore] BARK workspace updated to:', mode);
    },
    setBarkWorkspaceName: (name, expectedProjectId) => {
        if (expectedProjectId && get().activeProject?.project_id !== expectedProjectId) {
            return;
        }
        set({ barkWorkspaceName: name });
    },

    setMainWorkspaceView: (wsType) => {
        const project = get().activeProject;
        if (project) {
            set((state) => ({
                activeProject: { ...project, active_workspace: wsType },
                pendingChanges: true,
                _changeRevision: state._changeRevision + 1,
            }));
            get().saveWorkspaceNow(project.user_id);
        }
    },

    proposeWorkspaceSwitch: (proposal) => {
        set({ pendingWorkspaceSwitch: proposal });
        console.log('[ProjectStore] Workspace switch proposed:', proposal.to_workspace, '-', proposal.reason);
    },

    acceptWorkspaceSwitch: () => {
        const state = get();
        if (state.pendingWorkspaceSwitch) {
            const targetWorkspace = state.pendingWorkspaceSwitch.to_workspace;
            get().setActiveWorkspace(targetWorkspace);
        }
    },

    rejectWorkspaceSwitch: () => {
        set({ pendingWorkspaceSwitch: null });
        console.log('[ProjectStore] Workspace switch rejected');
    },

    addUserFinding: (finding) => {
        const state = get();
        if (!state.dictWorkspace) {
            console.warn('[ProjectStore] Cannot add finding: no active workspace');
            return;
        }
        const updatedFindings = {
            ...state.dictWorkspace.findings,
            [finding.finding_id]: finding,
        };
        set({
            dictWorkspace: {
                ...state.dictWorkspace,
                findings: updatedFindings,
            },
        });
        console.log('[ProjectStore] User finding added:', finding.finding_id);
    },

    setProductionDocument: (type, url) => {
        const state = get();
        set({
            productionState: {
                ...state.productionState,
                ...(type === 'doc' ? { googleDocUrl: url } : { googleSheetUrl: url }),
            },
        });
        console.log('[ProjectStore] Set production document:', type, url);
    },

    selectFindingForProduction: (findingId) => {
        const state = get();
        if (!state.productionState.selectedFindingIds.includes(findingId)) {
            set({
                productionState: {
                    ...state.productionState,
                    selectedFindingIds: [...state.productionState.selectedFindingIds, findingId],
                },
            });
        }
    },

    deselectFindingForProduction: (findingId) => {
        const state = get();
        set({
            productionState: {
                ...state.productionState,
                selectedFindingIds: state.productionState.selectedFindingIds.filter(id => id !== findingId),
            },
        });
    },

    setHandoffSyncNotification: (notification) => {
        const state = get();
        set({
            productionState: {
                ...state.productionState,
                handoffSyncNotification: notification,
            },
        });
        if (notification) {
            console.log('[ProjectStore] Handoff sync notification set:', notification.summary);
        }
    },

    clearHandoffSyncNotification: () => {
        const state = get();
        set({
            productionState: {
                ...state.productionState,
                handoffSyncNotification: null,
            },
        });
        console.log('[ProjectStore] Handoff sync notification cleared');
    },

    setActiveArtifact: (artifactId) => {
        const state = get();
        const artifact = state.productionState.artifacts.find(a => a.artifact_id === artifactId);
        set({
            productionState: {
                ...state.productionState,
                activeArtifactId: artifactId,
                // Also update legacy URL fields for backwards compatibility
                ...(artifact && artifact.type === 'doc' ? { googleDocUrl: artifact.url } : {}),
                ...(artifact && artifact.type === 'sheet' ? { googleSheetUrl: artifact.url } : {}),
            },
        });
        console.log('[ProjectStore] Set active artifact:', artifactId);
    },

    // Task & Institution Context actions
    updatePlan: (plan) => {
        set({ plan: { ...get().plan, ...plan } });
    },

    updateInstitutionProfile: (profile) => {
        set({ institutionProfile: { ...get().institutionProfile, ...profile } });
    },

    // Chat history actions
    addChatMessage: (role, content, metadata) => {
        const state = get();
        if (!state.activeProject) return;

        // Clear any existing timeout
        if (state._saveTimeoutId) {
            clearTimeout(state._saveTimeoutId);
        }

        const newMessage: ChatMessage = {
            message_id: crypto.randomUUID(),
            role,
            content,
            timestamp: new Date().toISOString(),
            metadata,
        };

        // Update local state immediately
        set({
            activeProject: {
                ...state.activeProject,
                chat_history: [...(state.activeProject.chat_history || []), newMessage],
            },
            pendingChanges: true,
            syncStatus: 'pending',
            _changeRevision: state._changeRevision + 1,
        });

        // Schedule auto-save
        const timeoutId = setTimeout(() => {
            const currentState = get();
            if (currentState.activeProject && currentState.pendingChanges) {
                const userId = currentState.activeProject.user_id;
                get().saveWorkspaceNow(userId);
            }
        }, AUTOSAVE_DELAY_MS);

        set({ _saveTimeoutId: timeoutId });
    },

    // Add chat message to local state only (no backend save)
    // Use when backend has already persisted the message
    addChatMessageLocal: (role: 'user' | 'assistant', content: string, metadata?: Record<string, any>) => {
        const state = get();
        if (!state.activeProject) return;

        const newMessage: ChatMessage = {
            message_id: crypto.randomUUID(),
            role,
            content,
            timestamp: new Date().toISOString(),
            metadata,
        };

        // Update local state only - don't trigger save since backend already has it
        set({
            activeProject: {
                ...state.activeProject,
                chat_history: [...(state.activeProject.chat_history || []), newMessage],
            },
            // Don't set pendingChanges - backend already has this data
        });
    },

    getChatHistory: () => {
        const state = get();
        return state.activeProject?.chat_history || [];
    },

    // Workspace operations (from API responses)
    applyWorkspaceOperations: (operations, expectedProjectId) => {
        const state = get();
        if (!state.activeProject || !operations || operations.length === 0) return;

        if (expectedProjectId && state.activeProject.project_id !== expectedProjectId) {
            console.log('[ProjectStore] Discarding workspace ops for', expectedProjectId, '— current:', state.activeProject.project_id);
            return;
        }

        // `requirements_updated` / `validation_complete` are pure refresh
        // signals: requirement state lives server-side (joined /requirements
        // projection), so there is no dict payload to fold in. Bump the
        // signal so open Spec views re-fetch, strip the ops, and skip the
        // autosave path when nothing else arrived (nothing local changed).
        // These two op types come from the backend only and sit outside the
        // typed WorkspaceOperationType union, hence the string compare.
        const isRequirementsSignal = (t: string) =>
            t === 'requirements_updated' || t === 'validation_complete';
        if (operations.some((op) => isRequirementsSignal(op.type as string))) {
            set({ requirementsRefreshSignal: get().requirementsRefreshSignal + 1 });
            operations = operations.filter((op) => !isRequirementsSignal(op.type as string));
            if (operations.length === 0) return;
        }

        // Clear any existing timeout
        if (state._saveTimeoutId) {
            clearTimeout(state._saveTimeoutId);
        }

        let updatedProject = { ...state.activeProject };

        // Dict workspace state (event-sourced, single source of truth)
        // Clone current dict state or initialize from empty
        let dictNodes = { ...(state.dictWorkspace?.nodes || {}) };
        let dictEdges = { ...(state.dictWorkspace?.edges || {}) };
        let dictFindings = { ...(state.dictWorkspace?.findings || {}) };
        // Reports are event-sourced like nodes: report ops mutate this dict
        // (the single source of truth the UI renders from), so the latest
        // version always reflects the edit log — agent set/append/edit AND
        // user edits (which echo back as the same ops) — without a reload.
        const dictReports = { ...(state.dictWorkspace?.reports || {}) };
        // Output-package doc files are event-sourced like reports: doc-file ops
        // mutate this dict (the source of truth the OutputPackageView renders).
        const dictDocFiles = { ...(state.dictWorkspace?.doc_files || {}) };
        let outputPackageMeta = state.dictWorkspace?.output_package_meta ?? null;
        let dictOnlineSupport = { ...(state.dictWorkspace?.online_support || {}) };
        let dictUploadedFiles = { ...(state.dictWorkspace?.uploaded_files || {}) };
        let dictWorkspaceUpdated = false;
        // BAR-192 Phase C: report-mutating ops void the grounding seal locally
        // (mirrors the backend reducer's stale-marking) so the badge reverts
        // to "Not validated" immediately, before the next /state reconcile.
        let reportMutated = false;
        let needsImmediateSave = false;  // For artifact ops that must save before reload_project

        for (const op of operations) {
            // Ensure reference_materials is initialized as structured object
            if (!updatedProject.reference_materials || typeof updatedProject.reference_materials === 'string') {
                updatedProject.reference_materials = createEmptyReferenceMaterials();
            }

            // Ensure findings is initialized
            if (!updatedProject.findings) {
                updatedProject.findings = {};
            }

            switch (op.type) {
                case 'set_research_query': {
                    // Set/update the research query (project description)
                    // Handle multiple possible field names from backend
                    const query = (op.data.query || op.data.research_query || op.data.research_focus) as string;
                    console.log('[ProjectStore] set_research_query data:', op.data);
                    if (query) {
                        updatedProject.description = query;
                        console.log('[ProjectStore] Applied set_research_query:', query.substring(0, 50));
                    } else {
                        console.warn('[ProjectStore] set_research_query: no query found in data');
                    }
                    break;
                }

                case 'add_toc_pointer': {
                    // Add a TOC pointer (e.g., "32013R0575")
                    const pointer = op.data.pointer as string;
                    if (pointer && !updatedProject.reference_materials.toc_pointers.includes(pointer)) {
                        updatedProject.reference_materials = {
                            ...updatedProject.reference_materials,
                            toc_pointers: [...updatedProject.reference_materials.toc_pointers, pointer],
                        };
                    }
                    console.log('[ProjectStore] Applied add_toc_pointer:', pointer);
                    break;
                }

                case 'add_rejected_path': {
                    // Add a rejected research path
                    const rejectedPath: RejectedPath = {
                        target: op.data.target as string,
                        reason: op.data.reason as string,
                        iteration: op.data.iteration as number,
                    };
                    updatedProject.reference_materials = {
                        ...updatedProject.reference_materials,
                        rejected_paths: [...updatedProject.reference_materials.rejected_paths, rejectedPath],
                    };
                    console.log('[ProjectStore] Applied add_rejected_path:', rejectedPath.target);
                    break;
                }

                case 'add_structural_insight': {
                    // Add a structural insight about regulation relationships
                    const insight: StructuralInsight = {
                        type: op.data.type as string,
                        detail: op.data.detail as string,
                        articles: op.data.articles as string[] || [],
                    };
                    updatedProject.reference_materials = {
                        ...updatedProject.reference_materials,
                        structural_insights: [...updatedProject.reference_materials.structural_insights, insight],
                    };
                    console.log('[ProjectStore] Applied add_structural_insight:', insight.type);
                    break;
                }

                case 'add_effective_query': {
                    // Add an effective query that yielded results
                    const query: EffectiveQuery = {
                        query: op.data.query as string,
                        tool: op.data.tool as string,
                        iteration: op.data.iteration as number,
                    };
                    updatedProject.reference_materials = {
                        ...updatedProject.reference_materials,
                        effective_queries: [...updatedProject.reference_materials.effective_queries, query],
                    };
                    console.log('[ProjectStore] Applied add_effective_query:', query.query);
                    break;
                }

                case 'add_iteration_reasoning': {
                    // Add iteration reasoning summary
                    const reasoning: IterationReasoning = {
                        iteration: op.data.iteration as number,
                        reasoning: op.data.reasoning as string,
                        next_focus: op.data.next_focus as string,
                    };
                    updatedProject.reference_materials = {
                        ...updatedProject.reference_materials,
                        iteration_reasoning: [...updatedProject.reference_materials.iteration_reasoning, reasoning],
                    };
                    console.log('[ProjectStore] Applied add_iteration_reasoning:', reasoning.iteration);
                    break;
                }

                case 'replace_reference_materials': {
                    // Replace entire structured reference materials
                    const newMaterials = op.data.reference_materials as ReferenceMaterials;
                    if (newMaterials) {
                        updatedProject.reference_materials = newMaterials;
                    }
                    console.log('[ProjectStore] Applied replace_reference_materials');
                    break;
                }

                case 'update_reference_materials': {
                    // Partial update to reference materials - merge provided fields
                    const materialsUpdate = op.data as Partial<ReferenceMaterials>;
                    updatedProject.reference_materials = {
                        ...updatedProject.reference_materials,
                        ...(materialsUpdate.toc_pointers && { toc_pointers: materialsUpdate.toc_pointers }),
                        ...(materialsUpdate.rejected_paths && { rejected_paths: materialsUpdate.rejected_paths }),
                        ...(materialsUpdate.structural_insights && { structural_insights: materialsUpdate.structural_insights }),
                        ...(materialsUpdate.effective_queries && { effective_queries: materialsUpdate.effective_queries }),
                        ...(materialsUpdate.iteration_reasoning && { iteration_reasoning: materialsUpdate.iteration_reasoning }),
                    };
                    console.log('[ProjectStore] Applied update_reference_materials');
                    break;
                }

                case 'add_evidence': {
                    // Add a new node to the workspace (dict-based, event-sourced)
                    const nodeId = op.data.node_id as string || crypto.randomUUID();

                    // Dict update (natural deduplication via key assignment)
                    dictNodes = upsertNode(dictNodes, {
                        node_id: nodeId,
                        node_type: op.data.node_type as string || 'concept',
                        label: op.data.label as string,
                        content: op.data.content as string | undefined,
                        relevance_note: op.data.relevance_note as string || '',
                        finding_ids: op.data.finding_id ? [op.data.finding_id as string] : [],
                    });
                    if (!dictEdges[nodeId]) {
                        dictEdges[nodeId] = [];
                    }
                    dictWorkspaceUpdated = true;

                    // Also add finding if provided
                    if (op.data.finding) {
                        const finding = op.data.finding as Finding;
                        dictFindings[finding.finding_id] = recordFindingToDictFinding(finding);
                    }
                    console.log('[ProjectStore] Applied add_evidence:', op.data.label, '(dict nodes:', Object.keys(dictNodes).length, ')');
                    break;
                }

                case 'add_connection': {
                    // Add an edge between nodes (dict-based, event-sourced)
                    const sourceNodeId = op.data.source_node_id as string;
                    const targetNodeId = op.data.target_node_id as string;

                    // Dict update (using addEdge helper for deduplication)
                    const edgeResult = addEdge(dictNodes, dictEdges, sourceNodeId, targetNodeId);
                    dictNodes = edgeResult.nodes;
                    dictEdges = edgeResult.edges;
                    dictWorkspaceUpdated = true;

                    console.log('[ProjectStore] Applied add_connection:', sourceNodeId, '->', targetNodeId);
                    break;
                }

                case 'update_evidence': {
                    // Update an existing node (dict-based, event-sourced)
                    const nodeId = op.data.node_id as string;
                    if (dictNodes[nodeId]) {
                        dictNodes = upsertNode(dictNodes, {
                            node_id: nodeId,
                            ...(op.data.label && { label: op.data.label as string }),
                            ...(op.data.content && { content: op.data.content as string }),
                            ...(op.data.node_type && { node_type: op.data.node_type as string }),
                        });
                        dictWorkspaceUpdated = true;
                    }
                    console.log('[ProjectStore] Applied update_evidence:', nodeId, op.data.node_type ? `(type→${op.data.node_type})` : '');
                    break;
                }

                case 'set_report': {
                    // Event-sourced report (like nodes): replace the derived
                    // report content. The single report per workspace is keyed
                    // by report_id (report_{compartment_id}); infer it from the
                    // op, the existing report, or the workspace compartment.
                    const reportId = (op.data.report_id as string)
                        || Object.keys(dictReports)[0]
                        || `report_${state.dictWorkspace?.compartment_id || 'default'}`;
                    dictReports[reportId] = {
                        report_id: reportId,
                        content: op.data.content as string,
                        title: (op.data.title as string) ?? dictReports[reportId]?.title,
                    };
                    dictWorkspaceUpdated = true;
                    reportMutated = true;
                    console.log('[ProjectStore] Applied set_report →', reportId);
                    break;
                }

                case 'append_to_report': {
                    // Append raw delta — matches the backend REPORT_APPENDED
                    // reducer (content += delta, no inserted separator) so the
                    // live view equals the reload-derived content exactly.
                    const reportId = (op.data.report_id as string)
                        || Object.keys(dictReports)[0]
                        || `report_${state.dictWorkspace?.compartment_id || 'default'}`;
                    const delta = (op.data.content as string) || '';
                    const existing = dictReports[reportId]?.content || '';
                    dictReports[reportId] = {
                        report_id: reportId,
                        content: `${existing}${delta}`,
                        title: dictReports[reportId]?.title,
                    };
                    dictWorkspaceUpdated = true;
                    reportMutated = true;
                    console.log('[ProjectStore] Applied append_to_report →', reportId);
                    break;
                }

                case 'edit_report': {
                    // Granular patch (mirrors the backend edit_report tool /
                    // REPORT_EDITED reducer): exact old_string→new_string on the
                    // current derived content. Lenient: skip if old_string no
                    // longer matches (the agent enforced uniqueness at creation).
                    const reportId = (op.data.report_id as string)
                        || Object.keys(dictReports)[0]
                        || `report_${state.dictWorkspace?.compartment_id || 'default'}`;
                    const existingReport = dictReports[reportId];
                    const oldStr = op.data.old_string as string;
                    const newStr = (op.data.new_string as string) ?? '';
                    const replaceAll = Boolean(op.data.replace_all);
                    if (existingReport && oldStr && existingReport.content.includes(oldStr)) {
                        const updated = replaceAll
                            ? existingReport.content.split(oldStr).join(newStr)
                            : existingReport.content.replace(oldStr, newStr);
                        dictReports[reportId] = { ...existingReport, content: updated };
                        dictWorkspaceUpdated = true;
                    reportMutated = true;
                        console.log('[ProjectStore] Applied edit_report →', reportId);
                    } else {
                        console.warn('[ProjectStore] edit_report: old_string not found, skipping', reportId);
                    }
                    break;
                }

                case 'set_doc_file': {
                    // Event-sourced output-package doc file (mirrors set_report).
                    // Content change clears any prior approval (an approval
                    // attests specific content).
                    const fid = op.data.file_id as string;
                    if (fid) {
                        const prev = dictDocFiles[fid];
                        dictDocFiles[fid] = {
                            file_id: fid,
                            content: (op.data.content as string) ?? '',
                            title: (op.data.title as string) ?? prev?.title,
                            template_id: (op.data.template_id as string) ?? prev?.template_id,
                            doc_kind: (op.data.doc_kind as string | null) ?? prev?.doc_kind ?? null,
                            derived_from_model_version:
                                (op.data.derived_from_model_version as number | null)
                                ?? prev?.derived_from_model_version
                                ?? null,
                            derived_from_model_hash:
                                (op.data.derived_from_model_hash as string | null)
                                ?? prev?.derived_from_model_hash
                                ?? null,
                            derived_from_run_id:
                                (op.data.derived_from_run_id as string | null)
                                ?? prev?.derived_from_run_id
                                ?? null,
                            dashboard_artifact_ref:
                                (op.data.dashboard_artifact_ref as string | null)
                                ?? prev?.dashboard_artifact_ref
                                ?? null,
                            dashboard_artifact_sha256:
                                (op.data.dashboard_artifact_sha256 as string | null)
                                ?? prev?.dashboard_artifact_sha256
                                ?? null,
                            dashboard_artifact_bytes:
                                (op.data.dashboard_artifact_bytes as number | null)
                                ?? prev?.dashboard_artifact_bytes
                                ?? null,
                            finding_refs: (op.data.finding_refs as any[]) ?? [],
                            regulatory_refs: (op.data.regulatory_refs as any[]) ?? [],
                            approach_choice_ids: (op.data.approach_choice_ids as string[]) ?? [],
                            classification_refs: (op.data.classification_refs as string[]) ?? [],
                            policy_ids: (op.data.policy_ids as string[]) ?? [],
                            source_artifact_refs: (op.data.source_artifact_refs as string[]) ?? [],
                            model_refs: (op.data.model_refs as string[]) ?? [],
                            doc_refs: (op.data.doc_refs as string[]) ?? [],
                            approved_by: null,
                            approved_at: null,
                            approver_role: prev?.approver_role ?? null,
                        };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied set_doc_file →', fid);
                    }
                    break;
                }

                case 'edit_doc_file': {
                    // Granular patch (mirrors edit_report). Clears approval.
                    const fid = op.data.file_id as string;
                    const existing = dictDocFiles[fid];
                    const oldStr = op.data.old_string as string;
                    const newStr = (op.data.new_string as string) ?? '';
                    const replaceAll = Boolean(op.data.replace_all);
                    if (existing && oldStr && existing.content.includes(oldStr)) {
                        const updated = replaceAll
                            ? existing.content.split(oldStr).join(newStr)
                            : existing.content.replace(oldStr, newStr);
                        dictDocFiles[fid] = { ...existing, content: updated, approved_by: null, approved_at: null };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied edit_doc_file →', fid);
                    } else {
                        console.warn('[ProjectStore] edit_doc_file: old_string not found, skipping', fid);
                    }
                    break;
                }

                case 'add_online_support': {
                    const support = op.data as OnlineSupport;
                    if (support?.online_support_id) {
                        dictOnlineSupport = {
                            ...dictOnlineSupport,
                            [support.online_support_id]: support,
                        };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied add_online_support:', support.online_support_id);
                    } else {
                        console.warn('[ProjectStore] add_online_support missing online_support_id:', op.data);
                    }
                    break;
                }

                case 'approve_doc_file': {
                    // Human per-document approval (actor="user"). Stamps the
                    // approver onto the current content.
                    const fid = op.data.file_id as string;
                    const existing = dictDocFiles[fid];
                    if (existing) {
                        dictDocFiles[fid] = {
                            ...existing,
                            approved_by: (op.data.approved_by as string) ?? 'user',
                            approved_at: (op.data.approved_at as string) ?? new Date().toISOString(),
                            approver_role: (op.data.approver_role as string) ?? existing.approver_role ?? null,
                        };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied approve_doc_file →', fid);
                    }
                    break;
                }

                case 'add_upload': {
                    const upload = op.data as any;
                    const uploadId = upload?.upload_id;
                    if (uploadId) {
                        dictUploadedFiles = {
                            ...dictUploadedFiles,
                            [uploadId]: upload,
                        };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied add_upload:', uploadId);
                    } else {
                        console.warn('[ProjectStore] add_upload missing upload_id:', op.data);
                    }
                    break;
                }

                case 'output_package_assembled': {
                    // The package's defined structure (model ref, required slots,
                    // provenance). Replace wholesale (re-assemble supersedes).
                    outputPackageMeta = (op.data as any) ?? null;
                    dictWorkspaceUpdated = true;
                    console.log('[ProjectStore] Applied output_package_assembled');
                    break;
                }

                case 'update_upload': {
                    const upload = op.data as any;
                    const uploadId = upload?.upload_id;
                    if (uploadId) {
                        const existingUpload = (dictUploadedFiles as any)[uploadId];
                        if (!existingUpload) {
                            console.warn('[ProjectStore] update_upload ignored for unknown upload:', uploadId);
                            break;
                        }
                        const safeUploadUpdate = { ...upload };
                        delete safeUploadUpdate.upload_id;
                        delete safeUploadUpdate.workspace_id;
                        delete safeUploadUpdate.created_at;
                        delete safeUploadUpdate.upload_status;
                        delete safeUploadUpdate.deleted_at;
                        delete safeUploadUpdate.owner_id;
                        delete safeUploadUpdate.storage_ref;
                        delete safeUploadUpdate.storage_provider;
                        const nextUpload = {
                            ...existingUpload,
                            ...safeUploadUpdate,
                        };
                        dictUploadedFiles = {
                            ...dictUploadedFiles,
                            [uploadId]: nextUpload,
                        };
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied update_upload:', uploadId);
                    } else {
                        console.warn('[ProjectStore] update_upload missing upload_id:', op.data);
                    }
                    break;
                }

                case 'remove_upload': {
                    const uploadId = (op.data as any)?.upload_id;
                    if (uploadId) {
                        const { [uploadId]: _removed, ...remainingUploads } = dictUploadedFiles;
                        dictUploadedFiles = remainingUploads;
                        dictWorkspaceUpdated = true;
                        console.log('[ProjectStore] Applied remove_upload:', uploadId);
                    } else {
                        console.warn('[ProjectStore] remove_upload missing upload_id:', op.data);
                    }
                    break;
                }

                case 'remove_evidence': {
                    // Remove a node from the workspace (dict-based, event-sourced)
                    const nodeIdToRemove = op.data.node_id as string;
                    if (nodeIdToRemove) {
                        // Dict update (using removeNode helper)
                        const removeResult = removeNode(dictNodes, dictEdges, nodeIdToRemove);
                        dictNodes = removeResult.nodes;
                        dictEdges = removeResult.edges;
                        // Also remove from dict findings
                        if (dictFindings[nodeIdToRemove]) {
                            const { [nodeIdToRemove]: _, ...remainingDictFindings } = dictFindings;
                            dictFindings = remainingDictFindings;
                        }
                        dictWorkspaceUpdated = true;

                        console.log('[ProjectStore] Applied remove_evidence:', nodeIdToRemove, '(dict nodes:', Object.keys(dictNodes).length, ')');
                    }
                    break;
                }

                case 'remove_connection': {
                    // Remove an edge between nodes (dict-based, event-sourced)
                    const sourceId = op.data.source_node_id as string;
                    const targetId = op.data.target_node_id as string;
                    if (sourceId && targetId) {
                        // Dict update (using removeEdge helper)
                        const removeEdgeResult = removeEdge(dictNodes, dictEdges, sourceId, targetId);
                        dictNodes = removeEdgeResult.nodes;
                        dictEdges = removeEdgeResult.edges;
                        dictWorkspaceUpdated = true;

                        console.log('[ProjectStore] Applied remove_connection:', sourceId, '->', targetId);
                    }
                    break;
                }

                // Phase 2 handlers (prepared for future use)
                case 'add_node_option':
                case 'select_node_option':
                case 'set_node_notes':
                case 'add_node_material':
                case 'remove_node_material':
                case 'set_active_compartment':
                case 'update_compartment_status': {
                    // Phase 2 operations - log for now, implement UI later
                    console.log('[ProjectStore] Phase 2 operation (not yet implemented):', op.type, op.data);
                    break;
                }

                case 'set_production_document': {
                    // Set production document URL (Google Doc/Sheet/Notebook created by agent)
                    const docType = op.data.document_type as 'doc' | 'sheet' | 'notebook';
                    const url = op.data.url as string;
                    if (docType && url) {
                        const currentState = get();
                        set({
                            productionState: {
                                ...currentState.productionState,
                                ...(docType === 'doc' ? { googleDocUrl: url } : {}),
                                ...(docType === 'sheet' ? { googleSheetUrl: url } : {}),
                                ...(docType === 'notebook' ? { jupyterNotebookUrl: url } : {}),
                            },
                        });
                        console.log('[ProjectStore] Applied set_production_document:', docType, url);
                    }
                    break;
                }

                case 'add_artifact': {
                    // Add a production artifact (Google Doc/Sheet) to the project
                    const artifact: ProductionArtifact = {
                        artifact_id: op.data.artifact_id || crypto.randomUUID(),
                        type: op.data.type as 'doc' | 'sheet' | 'notebook',
                        url: op.data.url as string,
                        title: op.data.title as string,
                        document_id: op.data.document_id as string || '',
                        handoff_ids: (op.data.handoff_ids as string[]) || [],
                        created_at: op.data.created_at as string || new Date().toISOString(),
                        created_by: op.data.created_by as 'agent' | 'user' || 'agent',
                    };

                    const currentState = get();
                    // Get existing artifacts from both productionState and project
                    const existingArtifacts = currentState.productionState.artifacts.length > 0
                        ? currentState.productionState.artifacts
                        : ((updatedProject as any).artifacts || []);

                    // Deduplicate by URL
                    const existingIndex = existingArtifacts.findIndex(
                        (a: ProductionArtifact) => a.url === artifact.url
                    );

                    let newArtifacts = [...existingArtifacts];
                    if (existingIndex >= 0) {
                        newArtifacts[existingIndex] = artifact;
                    } else {
                        newArtifacts.push(artifact);
                    }

                    // Update the project document for persistence
                    (updatedProject as any).artifacts = newArtifacts;

                    // Also update productionState for immediate UI feedback
                    set({
                        productionState: {
                            ...currentState.productionState,
                            artifacts: newArtifacts,
                            activeArtifactId: artifact.artifact_id,
                            // Also update legacy fields for backwards compatibility
                            ...(artifact.type === 'doc' ? { googleDocUrl: artifact.url } : {}),
                            ...(artifact.type === 'sheet' ? { googleSheetUrl: artifact.url } : {}),
                            ...(artifact.type === 'notebook' ? { jupyterNotebookUrl: artifact.url } : {}),
                        },
                    });
                    console.log('[ProjectStore] Applied add_artifact:', artifact.title, artifact.type);
                    // Flag for immediate save - artifacts must be persisted before reload_project
                    needsImmediateSave = true;
                    break;
                }

                case 'set_active_artifact': {
                    const artifactId = op.data.artifact_id as string;
                    const currentState = get();
                    const artifact = currentState.productionState.artifacts.find(
                        a => a.artifact_id === artifactId
                    );
                    if (artifact) {
                        set({
                            productionState: {
                                ...currentState.productionState,
                                activeArtifactId: artifactId,
                                ...(artifact.type === 'doc' ? { googleDocUrl: artifact.url } : {}),
                                ...(artifact.type === 'sheet' ? { googleSheetUrl: artifact.url } : {}),
                                ...(artifact.type === 'notebook' ? { jupyterNotebookUrl: artifact.url } : {}),
                            },
                        });
                        console.log('[ProjectStore] Applied set_active_artifact:', artifactId);
                    }
                    break;
                }

                default:
                    console.warn('[ProjectStore] Unknown workspace operation type:', op.type);
            }
        }

        // Update local state (both legacy and dict)
        const stateUpdate: Partial<ProjectState> = {
            activeProject: updatedProject,
            pendingChanges: true,
            syncStatus: 'pending',
            _changeRevision: state._changeRevision + 1,
        };

        // Update dict workspace if any dict operations were applied
        if (dictWorkspaceUpdated) {
            stateUpdate.dictWorkspace = {
                workspace_id: state.dictWorkspace?.workspace_id || `ws_${state.activeProject.project_id}`,
                sequence_number: (state.dictWorkspace?.sequence_number || 0) + 1,
                compartment_id: state.dictWorkspace?.compartment_id || 'default',
                nodes: dictNodes,
                edges: dictEdges,
                findings: dictFindings,
                markers: state.dictWorkspace?.markers || {},
                reports: dictReports,
                clusters: state.dictWorkspace?.clusters || {},
                models: state.dictWorkspace?.models || {},
                doc_files: dictDocFiles,
                output_package_meta: outputPackageMeta,
                online_support: dictOnlineSupport,
                uploaded_files: dictUploadedFiles,
                project: state.dictWorkspace?.project ?? null,
                // BAR-192: carry the grounding seal through the rebuild
                // (it would otherwise silently drop), voiding it when this
                // batch mutated the report — mirrors the backend reducer.
                report_grounding_seal: (() => {
                    const seal = state.dictWorkspace?.report_grounding_seal ?? null;
                    if (!seal) return null;
                    return reportMutated && !seal.stale
                        ? { ...seal, stale: true }
                        : seal;
                })(),
                // The latest validate_work review rides the rebuild the same
                // way; a report mutation in this batch marks it stale (graph
                // mutations are picked up on the next /state reload).
                report_review: (() => {
                    const review = state.dictWorkspace?.report_review ?? null;
                    if (!review) return null;
                    return reportMutated && !review.stale
                        ? { ...review, stale: true }
                        : review;
                })(),
                derived_at: new Date().toISOString(),
            };
        }

        set(stateUpdate as ProjectState);

        // For artifact operations, save immediately to beat the reload_project event
        // Otherwise use delayed auto-save
        if (needsImmediateSave) {
            console.log('[ProjectStore] Immediate save triggered for artifact operation');
            const userId = state.activeProject.user_id;
            get().saveWorkspaceNow(userId);
        } else {
            // Schedule auto-save
            const timeoutId = setTimeout(() => {
                const currentState = get();
                if (currentState.activeProject && currentState.pendingChanges) {
                    const userId = currentState.activeProject.user_id;
                    get().saveWorkspaceNow(userId);
                }
            }, AUTOSAVE_DELAY_MS);

            set({ _saveTimeoutId: timeoutId });
        }
    },

    // ==========================================================================
    // Dict Workspace State Actions (Event-Sourced from Report Library)
    // ==========================================================================

    /**
     * Load workspace state from Report Library event store.
     * This is the single source of truth for workspace data.
     */
    loadWorkspaceState: async (projectId, userId, opts) => {
        // Decide which workspace to reload based on the user's current view mode.
        // - In plan mode: always load the plan workspace (ws_{projectId})
        // - In research/IC/build mode: reload the spawned workspace the user is viewing
        // This prevents reload_project events from overwriting one compartment's data
        // with another's (e.g., research graph replaced by plan graph, or vice versa).
        const mode = get().workspaceMode;
        const spawnedWsId = get().dictWorkspace?.workspace_id;
        const planWsId = `ws_${projectId}`;
        const workspaceId = (mode === 'plan' || !spawnedWsId || spawnedWsId === planWsId)
            ? planWsId
            : spawnedWsId;
        console.log('[ProjectStore] Loading workspace state:', workspaceId, `(mode=${mode})`);

        const result = await apiGetWorkspaceState(workspaceId, userId, opts?.fresh !== true);

        const live = get().activeProject?.project_id;
        if (live && live !== projectId) {
            console.log('[ProjectStore] Discarding stale loadWorkspaceState for', projectId, '— current:', live);
            return false;
        }

        if (result.success && result.state) {
            set({
                dictWorkspace: buildDictWorkspaceStateFromResponse(result.state),
            });
            console.log('[ProjectStore] Workspace state loaded:', {
                nodes: Object.keys(result.state.nodes).length,
                findings: Object.keys(result.state.findings).length,
                clusters: Object.keys((result.state as any).clusters || {}).length,
                models: Object.keys(result.state.models || {}).length,
                sequence: result.state.sequence_number,
            });
            return true;
        }

        console.warn('[ProjectStore] Failed to load workspace state:', result.error);
        return false;
    },

    /**
     * Load a specific workspace by ID (for multi-workspace projects). (#89)
     */
    loadSpecificWorkspace: async (workspaceId: string, userId: string, opts) => {
        console.log('[ProjectStore] Loading specific workspace:', workspaceId);
        const callProjectId = get().activeProject?.project_id;
        const callProjectEpoch = opts?.expectedProjectEpoch ?? get()._projectEpoch;
        const callChangeRevision = opts?.expectedChangeRevision ?? get()._changeRevision;

        // Surface a loading state for direct user navigations (plan-node drill,
        // "Open node") so the click is acknowledged immediately rather than
        // sitting on a stale view while apiGetWorkspaceState is in flight (#89).
        // The internal re-hydration during loadProject passes showLoading:false
        // because the project-open overlay already covers that window.
        const showLoading = opts?.showLoading !== false;
        if (showLoading) set({ workspaceLoading: true });

        try {
            const result = await apiGetWorkspaceState(workspaceId, userId, opts?.fresh !== true);

            const live = get();
            if (
                live._projectEpoch !== callProjectEpoch
                || live._changeRevision !== callChangeRevision
                || (callProjectId && live.activeProject?.project_id !== callProjectId)
            ) {
                console.log(
                    '[ProjectStore] Discarding stale loadSpecificWorkspace',
                    workspaceId,
                    '— project changed',
                    callProjectId,
                    '->',
                    live.activeProject?.project_id,
                );
                return false;
            }

            if (result.success && result.state) {
                // Hydrate the institution profile slice when this workspace is the
                // institution_context compartment. Without this, drilling into the
                // Institution Context phase (plan train track / plan-node
                // double-click) loaded dictWorkspace but left institutionProfile
                // empty, so InstitutionProfilePanel rendered its placeholder even
                // though the data exists on the backend. Mirrors loadProject's
                // initial-open hydration via the shared converter.
                const hydratedInstitution = hydrateInstitutionProfileFromState(result.state);
                set({
                    dictWorkspace: buildDictWorkspaceStateFromResponse(result.state),
                    ...(hydratedInstitution ? { institutionProfile: hydratedInstitution } : {}),
                });
                return true;
            }

            console.warn('[ProjectStore] Failed to load workspace:', workspaceId, result.error);
            return false;
        } finally {
            if (showLoading && get()._projectEpoch === callProjectEpoch) {
                set({ workspaceLoading: false });
            }
        }
    },

    /**
     * Get the current dict workspace state.
     */
    getDictWorkspace: () => {
        return get().dictWorkspace;
    },

    // ==========================================================================
    // Firestore Events Subcollection Actions
    // ==========================================================================

    loadProjectEvents: async (projectId, userId) => {
        const state = get();
        set({ eventsLoading: true });

        console.log('[ProjectStore] Loading project events from Firestore...');
        // BAR-248: read from the backend-confirmed mark, not `lastEventSequence`
        // — the latter can sit above the server's real tail after an optimistic
        // `addLocalEvent`, which would silently skip persisted events — and
        // rewind below anything the backend may still rewrite in place.
        const since = resyncFloor(state.firestoreEvents, state.lastPersistedSequence);
        const result = await getProjectEventsFirestore(projectId, userId, since);

        // BAR-53: if the user switched projects during the fetch, don't
        // splice the old project's events into the new project's state.
        const liveProjectId = get().activeProject?.project_id;
        if (liveProjectId && liveProjectId !== projectId) {
            console.log('[ProjectStore] Discarding stale loadProjectEvents for', projectId, '— current:', liveProjectId);
            set({ eventsLoading: false });
            return false;
        }

        if (result.success && result.events) {
            const merged = mergeEventTail(get().firestoreEvents, since, result.events);
            const newLastSequence = merged.length > 0
                ? Math.max(...merged.map(e => e.sequence))
                : state.lastEventSequence;

            set({
                firestoreEvents: merged,
                lastEventSequence: newLastSequence,
                // The rewind must not drag the watermark backwards.
                lastPersistedSequence: Math.max(state.lastPersistedSequence, newLastSequence),
                eventsLoading: false,
            });

            console.log('[ProjectStore] Loaded', result.events.length, 'events, last sequence:', newLastSequence);
            return true;
        }

        console.warn('[ProjectStore] Failed to load events:', result.error);
        set({ eventsLoading: false });
        return false;
    },

    /**
     * Re-sync project events with the backend after streaming completes,
     * discarding any optimistic local events in favour of what was persisted.
     *
     * BAR-248: this used to refetch the ENTIRE history (`since_sequence=0`) on
     * every completed turn — for the sender AND, via the collab WebSocket, for
     * every other connected user. On prod's largest project (3,920 events and
     * growing ~100/day) that was a 580KB response backed by a full server-side
     * replay, several times per turn. It is now a TAIL read from the
     * backend-confirmed watermark.
     *
     * The replace-don't-append semantics are preserved: events at or below
     * the read floor are already confirmed and are kept as-is, while
     * everything above it — which includes the whole optimistic set
     * `addLocalEvent` created — is dropped and replaced by the authoritative
     * tail. A cold store has a watermark of 0, so the first reload after
     * opening a project still reads the full history.
     *
     * The floor is NOT simply the watermark. The backend rewrites some events
     * in place without moving their sequence, so `resyncFloor` pulls the read
     * back below anything still mutable — see its comment for why a pure
     * watermark would strand a finished background-research snapshot.
     */
    reloadProjectEvents: async (projectId, userId) => {
        const watermark = get().lastPersistedSequence;
        const since = resyncFloor(get().firestoreEvents, watermark);
        set({ eventsLoading: true });

        console.log('[ProjectStore] Re-syncing project events from backend since sequence', since);
        const result = await getProjectEventsFirestore(projectId, userId, since);

        // BAR-53: bail if the user has navigated to a different project
        // during the fetch. Otherwise the stale response would overwrite
        // the new project's events, leaving the chat panel showing the
        // previous project's history while the workspace correctly
        // reflects the current one.
        const liveProjectId = get().activeProject?.project_id;
        if (liveProjectId && liveProjectId !== projectId) {
            console.log('[ProjectStore] Discarding stale reloadProjectEvents for', projectId, '— current:', liveProjectId);
            set({ eventsLoading: false });
            return false;
        }

        if (result.success && result.events) {
            const merged = mergeEventTail(get().firestoreEvents, since, result.events);
            const newLastSequence = merged.length > 0
                ? Math.max(...merged.map(e => e.sequence))
                : 0;

            set({
                firestoreEvents: merged,  // backend is the single source of truth at and above `since`
                lastEventSequence: newLastSequence,
                // The rewind must not drag the watermark backwards.
                lastPersistedSequence: Math.max(watermark, newLastSequence),
                eventsLoading: false,
            });

            console.log('[ProjectStore] Re-synced', result.events.length, 'new events (', merged.length, 'total), last sequence:', newLastSequence);
            return true;
        }

        console.warn('[ProjectStore] Failed to reload events:', result.error);
        set({ eventsLoading: false });
        return false;
    },

    loadEarlierEvents: async (projectId, userId) => {
        const state = get();
        const cursor = state.oldestLoadedSequence;
        // 0 = the whole log is loaded (a short project, or a pre-paging
        // load). 1 = we already hold the first event. Either way there is
        // nothing older to fetch.
        if (cursor <= 1 || state.earlierEventsLoading) return false;

        set({ earlierEventsLoading: true });
        const result = await getProjectEventsFirestore(projectId, userId, 0, {
            beforeSequence: cursor,
            limit: EVENT_PAGE_SIZE,
        });

        const live = get();
        // The user may have left mid-fetch, or the cursor may have moved
        // under us. Dropping the page is correct: prepending it to a
        // different project's history would corrupt the pane.
        if (
            live.activeProject?.project_id !== projectId
            || live.oldestLoadedSequence !== cursor
        ) {
            set({ earlierEventsLoading: false });
            return false;
        }
        if (!result.success || !result.events) {
            set({ earlierEventsLoading: false });
            return false;
        }

        // Page on `oldest_sequence`, NOT on how many events came back.
        //
        // A short page does not mean the start of the log: the server measures
        // the page, then strips feedback events from it, so a full 200-event
        // read can return 199. Treating that as "reached the start" set the
        // cursor to 1, hid the button, and made every older turn unreachable
        // for the rest of the session — silently, at whatever boundary a
        // feedback event happened to sit.
        //
        // `oldest_sequence` is computed from the RAW page server-side
        // (main.py, get_events) precisely so it is safe to page on. Only a
        // genuinely empty page ends the walk; a log whose first events were
        // filtered out costs one cheap empty fetch to discover that.
        const reachedStart = result.events.length === 0 || result.oldestSequence == null;
        set({
            firestoreEvents: [...result.events, ...live.firestoreEvents],
            oldestLoadedSequence: reachedStart ? 1 : result.oldestSequence!,
            earlierEventsLoading: false,
        });
        console.log('[ProjectStore] Loaded', result.events.length, 'earlier events before seq', cursor);
        return true;
    },

    clearProjectEvents: () => {
        set({
            firestoreEvents: [],
            lastEventSequence: 0,
            lastPersistedSequence: 0,
            eventsLoading: false,
            oldestLoadedSequence: 0,
            earlierEventsLoading: false,
        });
    },

    /**
     * Add a local event for immediate display during streaming.
     * This is used to show user messages immediately before backend persistence.
     * Events will be reconciled on next loadProjectEvents() call.
     */
    addLocalEvent: (event: Omit<ChatEventFirestore, 'event_id' | 'sequence'> & Partial<Pick<ChatEventFirestore, 'event_id' | 'sequence'>>) => {
        const state = get();
        // BAR-53: callers may capture a stale `activeProject` in a closure
        // and push an event tagged with the previous project's id after
        // the user has switched. Drop it — the backend will still persist
        // the event under the correct project (it's keyed by the request,
        // not by what's currently in the frontend store), so we just
        // avoid polluting the live project's chat panel.
        const currentProjectId = state.activeProject?.project_id;
        if (currentProjectId && event.project_id !== currentProjectId) {
            console.log('[ProjectStore] Discarding stale addLocalEvent for', event.project_id, '— current:', currentProjectId);
            return;
        }
        const newSequence = event.sequence ?? state.lastEventSequence + 1;
        const newEvent: ChatEventFirestore = {
            ...event,
            event_id: event.event_id || crypto.randomUUID(),
            sequence: newSequence,
        };
        if (state.firestoreEvents.some(existing => existing.event_id === newEvent.event_id)) {
            return;
        }
        set({
            firestoreEvents: [...state.firestoreEvents, newEvent],
            lastEventSequence: Math.max(state.lastEventSequence, newSequence),
        });
        console.log('[ProjectStore] Added local event:', event.type, 'seq:', newSequence);
    },

    seedConversationHistory: (projectId, userId, turns) => {
        // BAR-320: when a user starts a project from the home-page chat, the
        // discussion they just had with BARK lives only in the landing
        // component's local React state — the new project starts cold. Replay
        // those turns onto the project's event model so they (a) render in the
        // research chat and (b) reach the model: since BAR-1551 the orchestrator
        // loads the conversation from the PERSISTED event log, not from this
        // store. The persists below are fire-and-forget, so a caller that
        // auto-sends a first turn must let them land first, or that one turn
        // is answered without the seeded discussion.
        //
        // Mapping mirrors getDisplayHistory's turn grouping: 'user' ->
        // user_message (starts a turn), 'assistant' -> response (the answer
        // attached to that turn). We add the events locally for immediate
        // display — no reload happens in this flow because the just-created
        // project is already the active one (App's hydration guard short-
        // circuits) — and persist them in the background for durability across
        // a refresh/reopen.
        const clean = turns.filter((t) => t.content && t.content.trim());
        if (clean.length === 0) return;

        // Keep every seeded stamp comfortably before the auto-sent prefill turn
        // (which stamps `new Date()` at send time). getDisplayHistory orders by
        // timestamp first, so we space seeded events 1s apart and land the last
        // one ~1s in the past — a wide enough gap that no seeded event can
        // collide with the prefill's stamp (which would otherwise fall back to
        // sequence numbers that differ between local and post-reload state).
        const seedTimestampBase = Date.now() - clean.length * 1000;
        clean.forEach((t, i) => {
            const type = t.role === 'user' ? 'user_message' : 'response';
            const timestamp = new Date(seedTimestampBase + i * 1000).toISOString();
            get().addLocalEvent({ project_id: projectId, type, content: t.content, timestamp });
            persistEvent(projectId, userId, type, t.content, undefined, timestamp).catch((err) => {
                console.warn('[ProjectStore] seedConversationHistory persist failed for', type, err);
            });
        });
    },

    autoTitleProject: async (projectId, userId, seedText, placeholderName, history = []) => {
        // Best-effort: generate a short, PII-safe title and rename the project.
        // Optimistically reflect the new name in the active-project slice (header)
        // and the cached project list ("Your work" / library) — same pattern as
        // EditableProjectTitle — so the UI updates without a refetch. On any
        // failure the project keeps the placeholder name createProject gave it.
        try {
            const title = await generateProjectTitle(seedText, history);
            if (!title || title === placeholderName) return;
            // Stale-name guard: the title call is a 1-3s LLM round-trip. If the
            // active project's name no longer matches the placeholder we created
            // with, someone renamed it meanwhile (e.g. the user via
            // EditableProjectTitle) — don't clobber their choice.
            const before = get();
            if (before.activeProject?.project_id === projectId
                && before.activeProject.name !== placeholderName) {
                return;
            }
            const result = await apiRenameProject(projectId, userId, title);
            if (!result.success) return;
            const state = get();
            if (state.activeProject?.project_id === projectId) {
                // Re-check after the (shorter) rename round-trip too.
                if (state.activeProject.name !== placeholderName) return;
                set({ activeProject: { ...state.activeProject, name: title } });
            }
            set({
                projectList: get().projectList.map((p) =>
                    p.project_id === projectId ? { ...p, name: title } : p
                ),
            });
        } catch (err) {
            console.warn('[ProjectStore] autoTitleProject failed:', err);
        }
    },

    getProjectEvents: () => {
        const state = get();
        return state.firestoreEvents;
    },

    /**
     * Convert Firestore events to display history format.
     * Groups events by message_id (turn) and returns chronological message list.
     * This replaces the old chat_history with nested activityLog approach.
     */
    getDisplayHistory: () => {
        const state = get();
        // Defend against cross-project event leaks (BAR-53): an in-flight
        // reload from a previous project can write its events into
        // `firestoreEvents` after the user has switched. Filter to the
        // active project so the chat panel renders only its own turns even
        // if a stale writer slipped past the action-level guards below.
        const currentProjectId = state.activeProject?.project_id;
        const allEvents = state.firestoreEvents;
        const events = currentProjectId
            ? allEvents.filter(e => !e.project_id || e.project_id === currentProjectId)
            : allEvents;
        if (!events || events.length === 0) return [];

        // Sort events by timestamp (chronological order).
        // Timestamp is more reliable than sequence for ordering because frontend-originated
        // events (e.g. workspace_switch) get persisted asynchronously and receive late
        // sequence numbers, causing them to appear at the end instead of in-place.
        const sortedEvents = [...events].sort((a, b) => {
            const tA = new Date(a.timestamp).getTime() || 0;
            const tB = new Date(b.timestamp).getTime() || 0;
            if (tA !== tB) return tA - tB;
            // Fall back to sequence for events with identical timestamps
            return a.sequence - b.sequence;
        });

        const feedbackTargetMessageIds = new Set<string>();
        for (const event of sortedEvents) {
            if (event.type !== 'feedback') continue;
            const targetMessageId = event.metadata?.target_message_id || event.message_id;
            if (targetMessageId && typeof targetMessageId === 'string') {
                feedbackTargetMessageIds.add(targetMessageId);
            }
        }

        // Group events by message_id (turn)
        // Each turn starts with user_message and includes all subsequent events until the next user_message
        const displayMessages: Array<{
            sender: 'user' | 'bark';
            message: string;
            user_name?: string;
            user_id?: string;
            activityLog?: Array<ActivityLogEntry>;
            thinking?: string;
            messageId?: string;
            feedbackProvided?: boolean;
            sources_used?: MessageSourcesUsed;
            uploadedFiles?: any[];
        }> = [];

        type DisplayTurn = {
            userMessage: string | null;
            userName: string | undefined;
            userId: string | undefined;
            uploadedFiles: any[];
            activityLog: Array<ActivityLogEntry>;
            thinking: string | null;
            responseContent: string | null;
            messageId: string | null;
            sourcesUsed: MessageSourcesUsed | null;
        };

        let currentTurn: DisplayTurn | null = null;
        // BAR-1270: messages an external agent wrote through the build-write
        // channel (report-library stamps them author='external_agent'), e.g. the
        // note of an approval relayed over the Build MCP. They are the agent's
        // own messages: never part of a user's turn, and never answering one.
        // Each is shown on its own, after the turn that was open when it
        // arrived. The server-side memory fold skips them entirely
        // (workflow_orchestrator_service/app/persistence/conversation_history.py).
        let agentTurns: DisplayTurn[] = [];

        const pushTurn = (turn: DisplayTurn) => {
            // Add user message if exists
            if (turn.userMessage !== null) {
                displayMessages.push({
                    sender: 'user',
                    message: turn.userMessage,
                    user_name: turn.userName,
                    user_id: turn.userId,
                    uploadedFiles: turn.uploadedFiles,
                });
            }

            // Heal stale research_progress events: if the turn has a tool_result
            // for research_workflow, the research finished — override isActive to false.
            // This handles inline mode (where the backend placeholder is never PATCHed)
            // and background mode edge cases (PATCH failed, server restarted, etc.).
            const hasResearchResult = turn.activityLog.some(
                e => e.type === 'tool_result' && e.content.includes('research_workflow')
            );
            if (hasResearchResult) {
                turn.activityLog = turn.activityLog.map(e => {
                    if (e.type !== 'research_progress') return e;
                    try {
                        const data = JSON.parse(e.content);
                        if (data.isActive) {
                            return { ...e, content: JSON.stringify({ ...data, isActive: false, currentPhase: 'complete' }) };
                        }
                    } catch { /* ignore malformed */ }
                    return e;
                });
            }

            // Add assistant message with activityLog
            if (turn.activityLog.length > 0 || turn.responseContent) {
                displayMessages.push({
                    sender: 'bark',
                    message: turn.responseContent || '',
                    activityLog: turn.activityLog,
                    thinking: turn.thinking || undefined,
                    messageId: turn.messageId || undefined,
                    feedbackProvided: turn.messageId
                        ? feedbackTargetMessageIds.has(turn.messageId)
                        : false,
                    sources_used: turn.sourcesUsed || undefined,
                });
            }
        };

        const finalizeTurn = () => {
            if (currentTurn) {
                pushTurn(currentTurn);
            }
            agentTurns.forEach(pushTurn);
            agentTurns = [];
        };

        const appendToTurn = (turn: DisplayTurn, event: ChatEventFirestore, eventTimestamp: number) => {
            // Capture message_id from events (set by backend emitter on all research events)
            if (!turn.messageId && event.message_id) {
                turn.messageId = event.message_id;
            }
            // Add to the turn's activity log
            if (event.type === 'thinking') {
                turn.thinking = event.content;
                turn.activityLog.push({
                    type: 'thinking_block',
                    content: event.content,
                    timestamp: eventTimestamp,
                });
            } else if (event.type === 'tool_call') {
                turn.activityLog.push({
                    type: 'tool_call',
                    content: event.content,
                    timestamp: eventTimestamp,
                });
            } else if (event.type === 'tool_result') {
                turn.activityLog.push({
                    type: 'tool_result',
                    content: event.content,
                    timestamp: eventTimestamp,
                });
            } else if (event.type === 'research_progress') {
                turn.activityLog.push({
                    type: 'research_progress',
                    content: event.content,
                    timestamp: eventTimestamp,
                });
            } else if (event.type === 'response') {
                turn.responseContent = event.content;
                turn.activityLog.push({
                    type: 'response',
                    content: event.content,
                    timestamp: eventTimestamp,
                    // BAR-1270: carry the server's authorship stamp to the
                    // renderer. Undefined for every unstamped event, which
                    // is what keeps existing turns rendering as they do.
                    author: event.author,
                    onBehalfOf: event.on_behalf_of,
                });
            } else if (event.type === 'user_question') {
                turn.activityLog.push({
                    type: 'user_question',
                    content: event.content,
                    timestamp: eventTimestamp,
                });
            } else if (event.type === 'sources_used') {
                // Per-message source attribution (JSON in content). Drives
                // the source icons under the answer. Not shown in the timeline.
                try {
                    turn.sourcesUsed = JSON.parse(event.content);
                } catch { /* ignore malformed */ }
            }
            // waiting_on_data (BAR-1227 durable park record, JSON content)
            // deliberately falls through unrendered: the missing-inputs
            // list rides on the execute_model tool result to the MCP
            // caller, and the turn's rendered tool_result/response events
            // already narrate the parked outcome on this timeline.
        };

        for (const event of sortedEvents) {
            // Use actual timestamp from event
            const eventTimestamp = new Date(event.timestamp).getTime() || 0;

            if (event.type === 'feedback') {
                continue;
            }

            if (event.author === 'external_agent') {
                // An external agent's own message (see agentTurns above). Events
                // sharing a message_id stay one message.
                let agentTurn = agentTurns[agentTurns.length - 1];
                if (!agentTurn || !event.message_id || agentTurn.messageId !== event.message_id) {
                    agentTurn = {
                        userMessage: null,
                        userName: undefined,
                        userId: undefined,
                        uploadedFiles: [],
                        activityLog: [],
                        thinking: null,
                        responseContent: null,
                        messageId: null,
                        sourcesUsed: null,
                    };
                    agentTurns.push(agentTurn);
                }
                appendToTurn(agentTurn, event, eventTimestamp);
                continue;
            }

            if (event.type === 'workspace_switch') {
                try {
                    const data = JSON.parse(event.content);
                    if (data.initiator === 'agent') {
                        // Agent-initiated: belongs inside the current BARK turn's activity timeline
                        // If there's no active turn, silently discard (agent switch before first message)
                        if (currentTurn) {
                            // Deduplicate by {from, to} pair — stale Firestore events from
                            // prior sessions can accumulate with identical pairs
                            const alreadyInLog = currentTurn.activityLog.some(e => {
                                if (e.type !== 'workspace_switch') return false;
                                try {
                                    const existing = JSON.parse(e.content);
                                    return existing.from === data.from && existing.to === data.to;
                                } catch { return false; }
                            });
                            if (!alreadyInLog) {
                                currentTurn.activityLog.push({
                                    type: 'workspace_switch',
                                    content: event.content,
                                    timestamp: eventTimestamp,
                                });
                            }
                        }
                    } else {
                        // User-initiated: standalone divider between turns
                        finalizeTurn();
                        currentTurn = null;
                        const modeLabels: Record<string, string> = { plan: 'Plan', institution_context: 'Institution Context', research: 'Research', production: 'Build' };
                        const targetLabel = modeLabels[data.to] || data.to;
                        displayMessages.push({
                            sender: 'user',
                            message: `[workspace_switch] You switched to ${targetLabel}`,
                        });
                    }
                } catch {
                    // Ignore malformed workspace_switch events
                }
                continue;
            } else if (event.type === 'user_message') {
                // Finalize previous turn
                finalizeTurn();
                // Start new turn
                currentTurn = {
                    userMessage: event.content,
                    userName: event.metadata?.user_name,
                    userId: event.metadata?.user_id,
                    uploadedFiles: Array.isArray(event.metadata?.uploaded_files)
                        ? (event.metadata?.uploaded_files || [])
                        : [],
                    activityLog: [],
                    thinking: null,
                    responseContent: null,
                    messageId: null,
                    sourcesUsed: null,
                };
            } else if (currentTurn) {
                appendToTurn(currentTurn, event, eventTimestamp);
            }
        }

        // Finalize last turn
        finalizeTurn();

        return displayMessages;
    },
}));
